import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import AnimatedBox from './AnimatedBox';
import LastMonthLeaders from './LastMonthLeaders';
import FavoritesWidget from './FavoritesWidget';
import MarketDemandWidget from './MarketDemandWidget';
import MonthSnapshot from './MonthSnapshot';
import AnalyzeMonthSnapshot from './AnalyzeMonthSnapshot';
import ReadingListWidget from './ReadingListWidget';
import ActionsPlaybookSnippet from './ActionsPlaybookSnippet';
import GoalsWidget from './GoalsWidget';
import SimpleGoalsWidget from './SimpleGoalsWidget';
import AdIntelligenceWidget from './AdIntelligenceWidget';
import CompareBusinessWidget from './CompareBusinessWidget';
import ContentTemplatesWidget from './ContentTemplatesWidget';
import LocalyticsScoreWidget from './LocalyticsScoreWidget';
import TrafficTrendsWidget from './TrafficTrendsWidget';
import KeywordIntelligenceWidget from './KeywordIntelligenceWidget';
import ReviewTotalsWidget from './ReviewTotalsWidget';
import WebTrafficWidget from './WebTrafficWidget';
import SentimentAnalysisWidget from './SentimentAnalysisWidget';
import DashboardLinkBubbles from './DashboardLinkBubbles';
import OnboardingStatusPanel from './OnboardingStatusPanel';
import {
  Box,
  SimpleGrid,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Button,
  Icon,
  IconButton,
  Flex,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Badge,
  Avatar,
  Progress,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Link,
  List,
  ListItem,
  Collapse,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  Grid,
  UnorderedList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tooltip,
  CloseButton,
  SlideFade,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  keyframes,
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend,
  ReferenceLine,
  LabelList,
  ScatterChart,
  Scatter,
  ZAxis
} from 'recharts';
import {
  FaChartLine,
  FaChartBar,
  FaChartPie,
  FaSmile,
  FaSearch,
  FaLightbulb,
  FaTrophy,
  FaArrowUp,
  FaArrowDown,
  FaBuilding,
  FaCrown,
  FaStoreAlt,
  FaRegStar,
  FaStar,
  FaTools,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaBalanceScale,
  FaBullseye,
  FaTasks,
  FaComment, // Added FaComment for feedback button
  FaDollarSign,
  FaCheck,
  FaGlobe,
  FaSearchDollar,
  FaMedal,
  FaAward,
  FaBookOpen,
  FaRocket,
  FaArrowRight,
  FaFilter,
  FaSortAmountDown
} from 'react-icons/fa';
import { MdTrendingUp } from 'react-icons/md';
import { LuSparkles, LuSend } from 'react-icons/lu';
import ReactMarkdown from 'react-markdown';
import getYearData from '../services/yearService';
import { getReviews, getHistoricalTraffic } from '../services/reviewService';
import { getBusinessImages } from '../services/imageService';
import { getLocalyticsRankings, formatRanking, getRankingColor } from '../services/localyticsRankingService';
import { calculateSeoScoreGrowth, getSeoScoreColor } from '../utils/competitiveIntelHelpers';
import { logger } from '../utils/logger';
import { getDomainStrengthOrEstimate, isDomainStrengthEstimated, getTrafficValue } from '../utils/competitiveIntelHelpers';
import BusinessReviewModal from './BusinessReviewModal';
import { useUser } from '../contexts/UserContext';
import { getWelcomeMessage } from '../services/userService';
import { getPricingDashboard, PRICE_TIERS } from '../services/pricingService';

const silverOutlineGlowHome = keyframes`
  0% { box-shadow: none; }
  25% { box-shadow: 0 0 0 3px rgba(215, 215, 215, 1), 0 0 18px rgba(192, 192, 192, 0.7); }
  50% { box-shadow: 0 0 0 5px rgba(230, 230, 230, 1), 0 0 28px rgba(180, 180, 180, 0.85); }
  75% { box-shadow: 0 0 0 3px rgba(215, 215, 215, 1), 0 0 18px rgba(192, 192, 192, 0.7); }
  100% { box-shadow: none; }
`;

// Custom Legend Component for Pie Charts
const CustomPieLegend = (props) => {
  const { payload } = props;
  // const textColor = useColorModeValue('gray.700', 'gray.200'); // Not needed if only showing swatch

  if (!payload || payload.length === 0) {
    return null;
  }

  return (
    <HStack
      spacing={3}
      wrap='wrap'
      justify='center'
      mt={3}
      cursor='default'
    >
      {payload.map((entry, index) => (
        <Tooltip
          key={`item-${index}`}
          label={entry.value}
          placement='top'
          hasArrow
        >
          <HStack
            as='span'
            spacing={1.5}
            align='center'
          >
            <Box
              as='span'
              w='10px'
              h='10px'
              bg={entry.payload.fill || entry.color}
              borderRadius='sm'
            />
            {/* <Text fontSize="xs" color={textColor}>{entry.value.length > 10 ? `${entry.value.substring(0,10)}...` : entry.value}</Text> */}
            {/* No default text, only color swatch. Full name on hover via Tooltip */}
          </HStack>
        </Tooltip>
      ))}
    </HStack>
  );
};

// Dashboard Card Component - Defined outside HomePage to prevent recreation on re-renders
const DashboardCard = ({
  title,
  icon,
  description,
  link,
  color,
  bgImage,
  index = 0
}) => {
  // Use hooks inside the component for theme colors and navigation
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardHoverBg = useColorModeValue('gray.50', 'gray.700');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const navigate = useNavigate();

  return (
    <AnimatedBox
      animation='slideUp'
      animationKey={`dashboard-card-${link}`}
      delay={0.1 + index * 0.05}
      duration={0.4}
      as={Card}
      bg={bgColor}
      borderWidth='1px'
      borderColor={borderColor}
      borderRadius='lg'
      overflow='hidden'
      boxShadow='sm'
      _hover={{
        transform: 'translateY(-4px)',
        shadow: 'md',
        borderColor: 'blue.400',
        bg: cardHoverBg
      }}
      transition='all 0.2s'
      cursor='pointer'
      onClick={() => navigate(link)}
      position='relative'
      h='100%'
    >
      {bgImage && (
        <Box
          position='absolute'
          top={0}
          right={0}
          bottom={0}
          left={0}
          bgImage={bgImage}
          bgSize='cover'
          bgPosition='center'
          opacity={0.07}
          pointerEvents='none'
        />
      )}
      <CardBody p={6}>
        <VStack
          align='start'
          spacing={4}
        >
          <AnimatedBox
            animation='scale'
            animationKey={`dashboard-card-icon-${link}`}
            delay={0.2 + index * 0.05}
            duration={0.3}
          >
            <Icon
              as={icon}
              boxSize={8}
              color={color}
            />
          </AnimatedBox>
          <AnimatedBox
            animation='fadeIn'
            animationKey={`dashboard-card-title-${link}`}
            delay={0.3 + index * 0.05}
            duration={0.4}
          >
            <Heading size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">{title}</Heading>
          </AnimatedBox>
          <Text
            color={subtitleColor}
            fontSize='sm'
            noOfLines={2}
          >
            {description}
          </Text>
        </VStack>
      </CardBody>
      <CardFooter
        pt={0}
        pb={4}
        px={6}
      >
        <AnimatedBox
          animation='fadeIn'
          animationKey={`dashboard-card-button-${link}`}
          delay={0.4 + index * 0.05}
          duration={0.5}
        >
          <Button
            variant='ghost'
            colorScheme='blue'
            size='sm'
            rightIcon={<Icon as={FaChartBar} />}
            _hover={{ transform: 'translateX(3px)' }}
            transition='all 0.2s'
            pl={0}
          >
            View Dashboard
          </Button>
        </AnimatedBox>
      </CardFooter>
    </AnimatedBox>
  );
};

// Component to make business names clickable
const BusinessTextRenderer = ({ children, businesses, onBusinessClick }) => {
  if (!children || !businesses?.length) return children;
  
  // Create a list of business names sorted by length (longest first to avoid partial matches)
  const businessNames = businesses
    .map(business => business.name)
    .filter(name => name && name.length > 2) // Filter out very short names
    .sort((a, b) => b.length - a.length);
  
  if (!businessNames.length) return children;
  
  // Function to replace business names with clickable components
  const renderWithBusinessLinks = (text) => {
    if (typeof text !== 'string') return text;
    
    let parts = [text];
    
    businessNames.forEach(businessName => {
      const newParts = [];
      
      parts.forEach(part => {
        if (typeof part !== 'string') {
          newParts.push(part);
          return;
        }
        
        // Case-insensitive search for business name
        const regex = new RegExp(`\\b${businessName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        const segments = part.split(regex);
        const matches = part.match(regex) || [];
        
        for (let i = 0; i < segments.length; i++) {
          if (segments[i]) newParts.push(segments[i]);
          if (matches[i]) {
            const business = businesses.find(b => 
              b.name.toLowerCase() === matches[i].toLowerCase()
            );
            if (business) {
              newParts.push(
                <Text
                  as="span"
                  key={`${businessName}-${i}`}
                  color="blue.500"
                  fontWeight="medium"
                  cursor="pointer"
                  textDecoration="underline"
                  _hover={{ color: 'blue.600', textDecoration: 'none' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onBusinessClick(business);
                  }}
                >
                  {matches[i]}
                </Text>
              );
            } else {
              newParts.push(matches[i]);
            }
          }
        }
      });
      
      parts = newParts;
    });
    
    return parts;
  };
  
  return renderWithBusinessLinks(children);
};

// Business Insight Tooltip Card - Mini version of BusinessInsightsDashboard card
const BusinessInsightTooltipCard = ({ 
  data, 
  businessImages, 
  trafficHistory,
  bgColor,
  borderColor 
}) => {
  const { brandName } = useUser();
  if (!data) return null;
  
  const business = data.businessObject || {};
  const imageUrl = businessImages?.[business.maps_uri];
  const trafficData = trafficHistory?.[business.maps_uri];
  
  // Calculate 3-month growth
  const calculateThreeMonthGrowth = (monthlyReviewCounts = {}, months = []) => {
    if (months.length < 3) return 0;
    const lastThreeMonths = months.slice(-3);
    return lastThreeMonths.reduce((sum, month) => sum + (monthlyReviewCounts[month] || 0), 0);
  };
  
  const threeMonthGrowth = calculateThreeMonthGrowth(
    business.historicalData?.monthlyReviewCounts,
    Object.keys(business.historicalData?.monthlyReviewCounts || {}).sort()
  );
  
  // Calculate sentiment percentage
  const sentimentBreakdown = business.historicalData?.sentimentBreakdown;
  const sentimentPercent = sentimentBreakdown 
    ? ((sentimentBreakdown.positive / 
        (sentimentBreakdown.positive + sentimentBreakdown.neutral + sentimentBreakdown.negative)) * 100).toFixed(0)
    : null;
  
  // Get sentiment emoji
  const getSentimentEmoji = () => {
    if (!sentimentBreakdown) return '😐';
    const total = sentimentBreakdown.positive + sentimentBreakdown.neutral + sentimentBreakdown.negative;
    const positiveRatio = total > 0 ? sentimentBreakdown.positive / total : 0;
    if (positiveRatio >= 0.8) return '😊';
    if (positiveRatio >= 0.6) return '🙂';
    if (positiveRatio >= 0.4) return '😐';
    return '😕';
  };
  
  // Get SEO/Domain strength score. When SpyFu data isn't available, fall
  // back to a reasonable estimate derived from rank, reviews, rating, and
  // any traffic data so we never have to render "N/A" to the user.
  const domainStrength = getDomainStrengthOrEstimate(business);
  const isDomainStrengthEst = isDomainStrengthEstimated(business);
  
  // Calculate cumulative review data for mini chart (no useMemo needed for tooltip)
  const calculateCumulativeReviewsData = () => {
    if (!business.historicalData?.monthlyReviewCounts) return [];
    
    const sortedData = Object.entries(business.historicalData.monthlyReviewCounts)
      .map(([month, count]) => ({
        date: new Date(month + '-01T00:00:00').getTime(),
        count: count,
        originalMonth: month
      }))
      .sort((a, b) => a.date - b.date)
      .slice(-12);
    
    const historicalTotal = sortedData.reduce((sum, item) => sum + (item.count || 0), 0);
    const actualTotal = business.rating_count || historicalTotal;
    const baselineReviews = Math.max(0, actualTotal - historicalTotal);
    
    let cumulative = baselineReviews;
    return sortedData.map(item => {
      cumulative += item.count || 0;
      return {
        month: new Date(item.originalMonth + '-01T00:00:00').toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit'
        }),
        cumulativeReviews: cumulative
      };
    });
  };
  
  const cumulativeReviewsData = calculateCumulativeReviewsData();
  
  // Helper to get business initials
  const getBusinessInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/).filter(word => word.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    const skipWords = ['and', 'the', 'a', 'an', 'of', 'in', 'for', '&'];
    const significantWords = words.filter(w => !skipWords.includes(w.toLowerCase()));
    if (significantWords.length >= 2) {
      return (significantWords[0][0] + significantWords[1][0]).toUpperCase();
    }
    if (significantWords.length === 1) {
      return significantWords[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + (words[1] ? words[1][0] : '')).toUpperCase();
  };
  
  return (
    <Box 
      bg={bgColor || 'white'} 
      borderRadius="lg" 
      p={4} 
      minW="320px"
      maxW="380px"
      boxShadow="xl"
    >
      {/* Business Name and Ranking Header */}
      <VStack align="stretch" spacing={3}>
        <Box>
          <Text fontWeight="bold" fontSize="md" mb={1} noOfLines={2}>
            {data.fullName}
          </Text>
          <Text 
            fontSize="lg" 
            fontWeight="bold" 
            color={data.rankingColor}
          >
            {brandName} Score: {data.localyticsScore} 
            <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500" ml={1}>
              (out of 100)
            </Text>
          </Text>
        </Box>
        
        {/* Business Image */}
        <Box 
          w="100%" 
          h="80px" 
          borderRadius="md" 
          overflow="hidden"
          bg="gray.100"
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={data.fullName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Flex
              w="100%"
              h="100%"
              justify="center"
              align="center"
              bg="gray.50"
              borderWidth="1px"
              borderColor={borderColor || 'gray.200'}
            >
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color="gray.400"
                letterSpacing="wider"
              >
                {getBusinessInitials(data.fullName)}
              </Text>
            </Flex>
          )}
        </Box>
        
        {/* Key Metrics Grid */}
        <SimpleGrid columns={2} spacing={2}>
          <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm">⭐</Text>
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color="gray.500">Total Reviews</Text>
              <Text fontSize="sm" fontWeight="bold">{data.totalReviews?.toLocaleString()}</Text>
            </VStack>
          </HStack>
          
          <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm">🏆</Text>
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color="gray.500">Avg Rating</Text>
              <Text fontSize="sm" fontWeight="bold">{data.avgRating?.toFixed(1)}/5.0</Text>
            </VStack>
          </HStack>
          
          <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm">📈</Text>
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color="gray.500">Growth (3-Mo)</Text>
              <Text fontSize="sm" fontWeight="bold" color={threeMonthGrowth > 0 ? "green.600" : "gray.700"}>
                {threeMonthGrowth > 0 ? `+${threeMonthGrowth}` : threeMonthGrowth}
              </Text>
            </VStack>
          </HStack>
          
          <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm">{getSentimentEmoji()}</Text>
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color="gray.500">Sentiment</Text>
              <Text fontSize="sm" fontWeight="bold">{sentimentPercent ? `${sentimentPercent}%` : `${data.sentimentScore?.toFixed(0)}%`}</Text>
            </VStack>
          </HStack>
          
          <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm">🌐</Text>
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color="gray.500">Monthly Visitors</Text>
              <Text fontSize="sm" fontWeight="bold">{data.monthlyVisitors?.toLocaleString() || 'N/A'}</Text>
            </VStack>
          </HStack>
          
          <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm">🎯</Text>
            <VStack spacing={0} align="start">
              <Text fontSize="xs" color="gray.500">
                SEO Score{isDomainStrengthEst ? ' (est.)' : ''}
              </Text>
              <Text fontSize="sm" fontWeight="bold" color={domainStrength >= 50 ? "green.600" : domainStrength >= 25 ? "orange.500" : "gray.700"}>
                {`${domainStrength}/100`}
              </Text>
            </VStack>
          </HStack>
        </SimpleGrid>
        
        {/* Mini Charts */}
        <VStack spacing={2} w="100%">
          {/* Total Reviews Chart */}
          <Box w="100%">
            <Text fontSize="xs" color="gray.600" mb={1} fontWeight="semibold">📊 Total Reviews Over Time</Text>
            <Box height="80px" bg="gray.50" borderRadius="md" p={1}>
              {cumulativeReviewsData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cumulativeReviewsData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <Line 
                      type="monotone" 
                      dataKey="cumulativeReviews" 
                      stroke="#48BB78" 
                      strokeWidth={2}
                      dot={false} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Flex h="100%" align="center" justify="center">
                  <Text fontSize="xs" color="gray.400">No data</Text>
                </Flex>
              )}
            </Box>
          </Box>
          
          {/* Traffic Trend Chart */}
          <Box w="100%">
            <Text fontSize="xs" color="gray.600" mb={1} fontWeight="semibold">🌐 Organic Traffic Trend</Text>
            <Box height="80px" bg="gray.50" borderRadius="md" p={1}>
              {trafficData && trafficData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trafficData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <Line 
                      type="monotone" 
                      dataKey="organicClicks" 
                      stroke="#3182CE" 
                      strokeWidth={2}
                      dot={false} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Flex h="100%" align="center" justify="center">
                  <Text fontSize="xs" color="gray.400">No data</Text>
                </Flex>
              )}
            </Box>
          </Box>
        </VStack>
        
        {/* Click hint */}
        <Text fontSize="xs" color="gray.400" textAlign="center" fontStyle="italic">
          Click on bar to view {brandName} Score dashboard
        </Text>
      </VStack>
    </Box>
  );
};

// AI Chat prompts that rotate in the input placeholder
const AI_CHAT_PROMPTS = [
  "Which competitor has the best reviews?",
  "Show me trending businesses this month",
  "Find negative reviews about pricing",
  "Generate a market overview report",
  "What are the top growth opportunities?",
  "Compare my top 3 competitors",
  "Summarize customer sentiment trends",
  "Which business is gaining the most reviews?",
];

const HomePage = ({ onOpenChat }) => {
  const [reviewsData, setReviewsData] = useState([]);
  const [yearData, setYearData] = useState([]);
  const [chatInputValue, setChatInputValue] = useState('');
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInsightExpanded, setIsInsightExpanded] = useState(false);
  // Active view state for 3-tier switch: 'analyze' | 'home' | 'beat'
  const [activeView, setActiveView] = useState('home');
  // Analyze view business selection state (stores hidden businesses)
  const [analyzeSelectedReviewBusinesses, setAnalyzeSelectedReviewBusinesses] = useState([]);
  const [analyzeSelectedTrafficBusinesses, setAnalyzeSelectedTrafficBusinesses] = useState([]);
  const [analyzeCompareBusinesses, setAnalyzeCompareBusinesses] = useState([]);
  const [winGoalsTargetPosition, setWinGoalsTargetPosition] = useState(5);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);
  const [localyticsData, setLocalyticsData] = useState(null);
  const [localyticsLoading, setLocalyticsLoading] = useState(false);
  const [combinedData, setCombinedData] = useState([]);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false); // Only show on first login of the day
  // State for business images - maps_uri -> image URL
  const [businessImages, setBusinessImages] = useState({});
  // State for historical traffic data - maps_uri -> timeSeries array
  const [trafficHistory, setTrafficHistory] = useState({});
  // State for pricing widget in Analyze section
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSearchQuery, setPricingSearchQuery] = useState('');
  const [pricingFilterTier, setPricingFilterTier] = useState('all');
  const [pricingSortBy, setPricingSortBy] = useState('price_level');
  const [urlInputHighlight, setUrlInputHighlight] = useState(false);
  const urlInputRef = useRef(null);

  const navigate = useNavigate();
  const { displayName, activity, isFirstLoginToday, brandName, onboardingStatus } = useUser();

  // Theme colors (Restored)
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardHoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const headingColor = useColorModeValue('gray.800', 'white');
  
  // Switch component colors
  const switchBgColor = useColorModeValue('gray.100', 'gray.700');
  const switchHoverBg = useColorModeValue('gray.200', 'gray.600');

  // Welcome message colors
  const welcomeBg = useColorModeValue('blue.50', 'blue.900');
  const welcomeBorder = useColorModeValue('blue.200', 'blue.700');
  const welcomeIconColor = useColorModeValue('blue.500', 'blue.300');
  const welcomeHeadingColor = useColorModeValue('blue.700', 'blue.200');
  const welcomeTextColor = useColorModeValue('blue.600', 'blue.300');
  const welcomeButtonColor = useColorModeValue('blue.600', 'blue.300');
  const welcomeButtonHoverBg = useColorModeValue('blue.100', 'blue.800');

  // Playbook card colors
  const playbookCardBg = useColorModeValue('white', 'gray.800');
  const playbookCardBorder = useColorModeValue('gray.200', 'gray.600');
  const playbookCardHoverBorder = useColorModeValue('blue.300', 'blue.500');
  const playbookHeadingColor = useColorModeValue('gray.800', 'white');
  const playbookButtonColor = useColorModeValue('gray.600', 'gray.300');

  // Colors for charts and visualizations - using useMemo to avoid recreation on every render
  const COLORS = useMemo(
    () => ({
      primary: '#0066ff',
      secondary: '#4299E1',
      success: '#48BB78',
      warning: '#ECC94B',
      danger: '#F56565',
      info: '#3182CE',
      charts: [
        '#2F80ED', // bright blue
        '#27AE60', // emerald green
        '#64748B', // slate gray
        '#F2994A', // orange
        '#EB5757', // bright red
        '#219EBC', // teal
        '#3B82F6', // blue
        '#475569' // dark slate
      ]
    }),
    []
  );

  // Dashboard data for searching - using useMemo to avoid recreation on every render
  const dashboardData = useMemo(
    () => [
      {
        id: 'business-insights',
        title: 'Business Insights',
        description:
          'Complete overview of business metrics and performance insights',
        link: '/dashboards/business-insights',
        icon: FaLightbulb,
        color: COLORS.primary,
        keywords: ['business', 'overview', 'performance', 'metrics', 'insights']
      },
      {
        id: 'market-trends',
        title: 'Month in Review',
        description:
          'Market-wide insights showing what customers love and biggest problems',
        link: '/dashboards/market-trends',
        icon: MdTrendingUp,
        color: COLORS.success,
        keywords: ['market', 'trends', 'insights', 'customer', 'problems', 'loves']
      },
      {
        id: 'total-reviews',
        title: 'Total Reviews',
        description: 'Track review volume and analyze rating trends.',
        link: '/dashboards/total-reviews',
        icon: FaChartBar,
        color: COLORS.secondary,
        keywords: [
          'reviews',
          'ratings',
          'volume',
          'trends',
          'performance',
          'total'
        ]
      },
      {
        id: 'gross-growth',
        title: 'Review Growth',
        description: 'Monitor review growth rates and identify opportunities.',
        link: '/dashboards/gross-growth',
        icon: FaChartLine,
        color: COLORS.success,
        keywords: [
          'growth',
          'acceleration',
          'opportunities',
          'rates',
          'monitor',
          'review'
        ]
      },
      {
        id: 'sentiment',
        title: 'Sentiment Analysis',
        description: 'Analyze customer sentiment and feedback trends',
        link: '/dashboards/sentiment',
        icon: FaSmile,
        color: COLORS.primary,
        keywords: ['sentiment', 'feedback', 'analysis', 'customer', 'trends']
      },
      {
        id: 'traffic',
        title: 'Traffic',
        description: 'Monitor website traffic and visitor analytics',
        link: '/dashboards/keyword-intelligence',
        icon: FaGlobe,
        color: COLORS.accent,
        keywords: [
          'traffic',
          'visitors',
          'analytics',
          'website',
          'web',
          'monitoring'
        ]
      },
      {
        id: 'seo',
        title: 'SEO',
        description: 'Track search rankings and keyword performance',
        link: '/dashboards/web-traffic',
        icon: FaSearchDollar,
        color: COLORS.secondary,
        keywords: [
          'seo',
          'search',
          'keywords',
          'rankings',
          'optimization',
          'visibility'
        ]
      },
      {
        id: 'playbook-goals',
        title: 'Playbook Goals',
        description:
          'Define and track strategic objectives for your business growth.',
        link: '/playbook/goals',
        icon: FaBullseye,
        color: COLORS.warning, // Using a distinct color, e.g., warning
        keywords: [
          'playbook',
          'goals',
          'objectives',
          'strategy',
          'planning',
          'tasks'
        ]
      },
      {
        id: 'playbook-actions',
        title: 'Playbook Actions',
        description:
          'Outline and manage specific actions to achieve your playbook goals.',
        link: '/playbook/actions',
        icon: FaTasks,
        color: COLORS.warning, // Using a distinct color, e.g., warning (or a different one if preferred)
        keywords: [
          'playbook',
          'actions',
          'tasks',
          'execution',
          'strategy',
          'implementation'
        ]
      }
    ],
    [COLORS]
  );

  // Rotate AI chat prompts every 3 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentPromptIndex((prev) => (prev + 1) % AI_CHAT_PROMPTS.length);
    }, 3000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const shouldHighlight = localStorage.getItem('highlightAfterSignup');
    if (shouldHighlight) {
      localStorage.removeItem('highlightAfterSignup');
      const timer = setTimeout(() => {
        setUrlInputHighlight(true);
        if (urlInputRef.current) {
          urlInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setUrlInputHighlight(false), 3200);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle AI chat input submission
  const handleChatSubmit = useCallback(() => {
    const message = chatInputValue.trim() || AI_CHAT_PROMPTS[currentPromptIndex];
    if (message && onOpenChat) {
      onOpenChat(message);
      setChatInputValue('');
    }
  }, [chatInputValue, currentPromptIndex, onOpenChat]);

  // Handle Enter key in chat input
  const handleChatKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChatSubmit();
    }
  }, [handleChatSubmit]);

  // Load Localytics Ranking data
  const loadLocalyticsData = useCallback(async () => {
    if (localyticsLoading) return; // Prevent multiple calls

    setLocalyticsLoading(true);
    try {
      logger.log('Loading Localytics Rankings...');
      const localyticsResponse = await getLocalyticsRankings();
      setLocalyticsData(localyticsResponse);
      logger.log('Localytics Rankings loaded successfully');
    } catch (error) {
      logger.error('Error loading Localytics Rankings:', error);
      // Don't show error to user - rankings are optional enhancement
    } finally {
      setLocalyticsLoading(false);
    }
  }, [localyticsLoading]);


  // Load data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        logger.log('Loading dashboard data...✨');
        const [reviewsResponse, yearResponse] = await Promise.all([
          getReviews(),
          getYearData()
        ]);
        setReviewsData(reviewsResponse);
        setYearData(yearResponse);
        
        // Load Localytics Rankings in background (non-blocking)
        loadLocalyticsData();
      } catch (error) {
        logger.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Create combined data when both reviewsData and yearData are loaded
  useEffect(() => {
    if (reviewsData?.length > 0 && yearData?.length > 0) {
      const combined = reviewsData.map((currentData) => {
        const historicalData = yearData.find(
          (yd) => yd.maps_uri === currentData.maps_uri
        );
        return {
          ...currentData,
          historicalData: historicalData?.data || {
            monthlyReviewCounts: {},
            monthlyRatings: {},
            sentimentBreakdown: {
              positive: 0,
              neutral: 0,
              negative: 0
            }
          }
        };
      });
      setCombinedData(combined);
    }
  }, [reviewsData, yearData]);

  // Fetch business images (async, non-blocking)
  useEffect(() => {
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          // Create a map of maps_uri -> image URL
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
        // Don't block UI on image fetch failure
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch historical traffic data (async, non-blocking)
  useEffect(() => {
    let mounted = true;
    
    const fetchTrafficData = async () => {
      try {
        const result = await getHistoricalTraffic();
        if (mounted && result?.businesses) {
          // Create a map of maps_uri -> timeSeries data
          const trafficMap = {};
          result.businesses.forEach(business => {
            if (business.mapsUri && business.timeSeries) {
              // Process time series to get chart-ready data
              const chartData = business.timeSeries
                .sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
                .slice(-12) // Last 12 months
                .map(point => ({
                  month: new Date(point.yearMonth + '-01T00:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit'
                  }),
                  organicClicks: point.organicClicks || 0
                }));
              trafficMap[business.mapsUri] = chartData;
            }
          });
          setTrafficHistory(trafficMap);
        }
      } catch (error) {
        logger.error('Error fetching traffic history:', error);
        // Don't block UI on traffic fetch failure
      }
    };

    fetchTrafficData();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch personalized welcome message (only on first login of the day)
  useEffect(() => {
    // Only fetch welcome message if this is the first login today
    if (isFirstLoginToday !== true) {
      return;
    }

    const fetchWelcomeMessage = async () => {
      try {
        const message = await getWelcomeMessage();
        setWelcomeMessage(message);
        setShowWelcome(true); // Enable popup display
      } catch (error) {
        logger.error('Error fetching welcome message:', error);
        // Set a default welcome if the API fails
        setWelcomeMessage({
          greeting: `Welcome back${displayName ? ', ' + displayName : ''}!`,
          message: 'Ready to explore your market intelligence?'
        });
        setShowWelcome(true); // Still show popup with default message
      }
    };
    fetchWelcomeMessage();
  }, [displayName, isFirstLoginToday]);

  // Auto-dismiss welcome notification after 10 seconds and trigger confetti
  useEffect(() => {
    if (welcomeMessage && showWelcome) {
      // Trigger confetti animation from top right
      const duration = 2000;
      const animationEnd = Date.now() + duration;
      
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        
        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }
        
        // Fire confetti from the top right area
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 0.9, y: 0.1 },
          colors: ['#3182CE', '#63B3ED', '#4299E1', '#2B6CB0', '#FBD38D', '#F6AD55']
        });
        
        confetti({
          particleCount: 2,
          angle: 150,
          spread: 45,
          origin: { x: 0.85, y: 0.15 },
          colors: ['#3182CE', '#63B3ED', '#4299E1', '#2B6CB0', '#FBD38D', '#F6AD55']
        });
      }, 100);
      
      // Auto-dismiss after 10 seconds
      const dismissTimer = setTimeout(() => {
        setShowWelcome(false);
      }, 10000);
      
      return () => {
        clearInterval(interval);
        clearTimeout(dismissTimer);
      };
    }
  }, [welcomeMessage, showWelcome]);

  // Fetch pricing data when switching to Analyze view
  useEffect(() => {
    const fetchPricingData = async () => {
      if (activeView === 'analyze' && !pricingData && !pricingLoading) {
        setPricingLoading(true);
        try {
          const data = await getPricingDashboard();
          setPricingData(data);
        } catch (error) {
          logger.error('Error fetching pricing data:', error);
        } finally {
          setPricingLoading(false);
        }
      }
    };
    fetchPricingData();
  }, [activeView, pricingData, pricingLoading]);

  // Create sorted arrays for the modal (similar to TotalReviewsDashboard)
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const sentimentBreakdown = business.historicalData?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0
      };
      const total = sentimentBreakdown.positive + sentimentBreakdown.neutral + sentimentBreakdown.negative;
      const sentimentScore = total > 0 
        ? ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) / total) * 100 
        : 0;
      return { ...business, sentimentScore, ...sentimentBreakdown, total };
    }).sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].map((business) => {
      const monthlyData = business.historicalData?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();
      if (months.length < 6) {
        return { ...business, threeMonthGrowth: 0 };
      }
      const lastThreeMonths = months.slice(-3);
      const previousThreeMonths = months.slice(-6, -3);
      const lastThreeSum = lastThreeMonths.reduce((sum, month) => sum + (monthlyData[month] || 0), 0);
      const prevThreeSum = previousThreeMonths.reduce((sum, month) => sum + (monthlyData[month] || 0), 0);
      const threeMonthGrowth = prevThreeSum > 0 ? ((lastThreeSum - prevThreeSum) / prevThreeSum) * 100 : 0;
      return { ...business, threeMonthGrowth };
    }).sort((a, b) => b.threeMonthGrowth - a.threeMonthGrowth);
  }, [combinedData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => {
        const trafficValue = getTrafficValue(business) || 0;
        return { ...business, trafficValue };
      })
      .filter((business) => business.trafficValue > 0)
      .sort((a, b) => b.trafficValue - a.trafficValue);
  }, [combinedData]);

  // Pricing widget data
  const pricingPieChartData = useMemo(() => {
    if (!pricingData?.market_summary?.tier_distribution) return [];
    const dist = pricingData.market_summary.tier_distribution;
    const TIER_CHART_COLORS = {
      '$': '#38A169',
      '$$': '#3182CE',
      '$$$': '#805AD5',
      '$$$$': '#DD6B20',
    };
    return Object.entries(dist)
      .filter(([, value]) => value > 0)
      .map(([tier, count]) => ({
        name: tier,
        value: count,
        color: TIER_CHART_COLORS[tier],
      }));
  }, [pricingData]);

  const pricingScatterData = useMemo(() => {
    if (!pricingData?.businesses) return [];
    return pricingData.businesses
      .filter(b => b.price_level && b.rating_avg != null && b.rating_avg > 0)
      .map(b => ({
        name: b.name,
        price_level: b.price_level,
        rating: b.rating_avg,
        reviews: b.rating_count || 10,
      }));
  }, [pricingData]);

  const filteredPricingBusinesses = useMemo(() => {
    if (!pricingData?.businesses) return [];
    let filtered = [...pricingData.businesses];
    if (pricingSearchQuery) {
      const query = pricingSearchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.name.toLowerCase().includes(query) ||
        b.primary_type?.toLowerCase().includes(query)
      );
    }
    if (pricingFilterTier !== 'all') {
      const tierLevel = parseInt(pricingFilterTier);
      filtered = filtered.filter(b => b.price_level === tierLevel);
    }
    switch (pricingSortBy) {
      case 'price_level':
        filtered.sort((a, b) => a.price_level - b.price_level);
        break;
      case 'price_level_desc':
        filtered.sort((a, b) => b.price_level - a.price_level);
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    return filtered;
  }, [pricingData, pricingSearchQuery, pricingFilterTier, pricingSortBy]);

  // Calculate key metrics
  const calculateMetrics = () => {
    // Make sure reviewsData has loaded and is not empty
    if (!reviewsData || !reviewsData.length) return null;

    // Log the raw data to confirm structure
    console.log('Data used for metrics:', reviewsData);

    // Find the first macro_insight from the reviewsData (assuming it's merged)
    let macroInsight = null;
    for (const business of reviewsData) {
      if (business.macro_insight) {
        macroInsight = business.macro_insight;
        console.log('Found macro_insight:', macroInsight); // Log if found
        break; // Found one, stop looking
      }
    }

    // If still null after checking reviewsData, try checking yearData as a fallback
    if (!macroInsight && yearData && yearData.length > 0) {
      console.log('Checking yearData for macro_insight...');
      for (const yd of yearData) {
        // Check both yd.macro_insight and yd.data?.macro_insight
        if (yd.macro_insight) {
          macroInsight = yd.macro_insight;
          console.log(
            'Found macro_insight in yearData (top-level):',
            macroInsight
          );
          break;
        } else if (yd.data?.macro_insight) {
          macroInsight = yd.data.macro_insight;
          console.log(
            'Found macro_insight in yearData (nested):',
            macroInsight
          );
          break;
        }
      }
    }

    if (!macroInsight) {
      console.log('Macro insight not found in reviewsData or yearData.');
    }

    // Sort businesses by total reviews
    const sortedByTotal = [...reviewsData].sort(
      (a, b) => (b.rating_count || 0) - (a.rating_count || 0)
    ); // Add guards for missing data
    const totalReviews = sortedByTotal.reduce(
      (sum, b) => sum + (b.rating_count || 0),
      0
    );
    
    // Calculate average rating with better handling for missing/invalid data
    // Filter to only businesses with valid rating_avg values
    const businessesWithRatings = sortedByTotal.filter(
      b => b.rating_avg !== undefined && b.rating_avg !== null && b.rating_avg > 0
    );
    const avgRating =
      businessesWithRatings.length > 0
        ? businessesWithRatings.reduce((sum, b) => sum + b.rating_avg, 0) /
          businessesWithRatings.length
        : sortedByTotal.length > 0 
          ? sortedByTotal.reduce((sum, b) => sum + (b.rating_avg || 0), 0) / sortedByTotal.length
          : 0;
    
    // Debug logging for average rating calculation
    if (avgRating === 0 && sortedByTotal.length > 0) {
      console.warn('Average rating is 0 despite having businesses. Sample data:', {
        totalBusinesses: sortedByTotal.length,
        businessesWithValidRatings: businessesWithRatings.length,
        sampleRatings: sortedByTotal.slice(0, 3).map(b => ({
          name: b.name,
          rating_avg: b.rating_avg,
          rating_count: b.rating_count
        }))
      });
    }

    // Calculate market activity
    const allMonthlyData = {};

    // Process business data to collect monthly review activity
    reviewsData.forEach((business) => {
      const yearInfo = yearData.find((yd) => yd.maps_uri === business.maps_uri);
      const monthlyData = yearInfo?.data?.monthlyReviewCounts || {};
      const months = Object.keys(monthlyData).sort();

      months.forEach((month) => {
        allMonthlyData[month] =
          (allMonthlyData[month] || 0) + (monthlyData[month] || 0);
      });
    });

    // Market activity trends
    const monthlyTrends = Object.entries(allMonthlyData)
      .sort((a, b) => new Date(a[0] + '-01T00:00:00') - new Date(b[0] + '-01T00:00:00'))
      .map(([month, count]) => {
        const d = new Date(month + '-01T00:00:00');
        return {
          month: d.toLocaleDateString('en-US', { month: 'short' }),
          year: d.getFullYear().toString().slice(-2),
          count,
          date: month
        };
      });

    // Calculate market momentum (last 3 months vs previous 3 months)
    const last6Months = monthlyTrends.slice(-6);
    const last3MonthsActivity = last6Months
      .slice(-3)
      .reduce((sum, m) => sum + m.count, 0);
    const previous3MonthsActivity = last6Months
      .slice(0, 3)
      .reduce((sum, m) => sum + m.count, 0);
    const marketMomentum =
      previous3MonthsActivity > 0
        ? ((last3MonthsActivity - previous3MonthsActivity) /
            previous3MonthsActivity) *
          100
        : 0;

    // Calculate market competition (standard deviation of review counts)
    const avgReviews = totalReviews / reviewsData.length;
    const variance =
      reviewsData.reduce(
        (sum, b) => sum + Math.pow(b.rating_count - avgReviews, 2),
        0
      ) / reviewsData.length;
    const competitionScore = (Math.sqrt(variance) / avgReviews) * 100;

    // Sentiment analysis
    const sentimentData = yearData
      .map((business) => {
        const sentimentBreakdown = business.data?.sentimentBreakdown || {
          positive: 0,
          neutral: 0,
          negative: 0
        };
        const total =
          sentimentBreakdown.positive +
          sentimentBreakdown.neutral +
          sentimentBreakdown.negative;
        return {
          name: business.name,
          score:
            total > 0
              ? ((sentimentBreakdown.positive +
                  sentimentBreakdown.neutral * 0.5) /
                  total) *
                100
              : 0,
          total,
          positive: sentimentBreakdown.positive,
          neutral: sentimentBreakdown.neutral,
          negative: sentimentBreakdown.negative
        };
      })
      .sort((a, b) => b.score - a.score);

    const avgSentiment =
      sentimentData.reduce((sum, business) => sum + business.score, 0) /
      sentimentData.length;

    // Market leaders
    const marketLeaders = sortedByTotal.slice(0, 10).map((business) => ({
      name: business.name,
      reviews: business.rating_count,
      rating: business.rating_avg,
      share: ((business.rating_count / totalReviews) * 100).toFixed(1)
    }));

    // Calculate type distribution
    const allTypes = new Set();
    const typeCount = {};
    reviewsData.forEach((business) => {
      (business.types || []).forEach((type) => {
        if (
          !type.includes('point_of_interest') &&
          !type.includes('establishment') &&
          !type.includes('food')
        ) {
          allTypes.add(type);
          typeCount[type] = (typeCount[type] || 0) + 1;
        }
      });
    });

    const typeDistribution = Object.entries(typeCount)
      .map(([type, count]) => ({
        name: type
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        value: ((count / reviewsData.length) * 100).toFixed(1),
        count
      }))
      .sort((a, b) => b.count - a.count);

    // Calculate primary type distribution
    const primaryTypes = {};
    reviewsData.forEach((business) => {
      const type = business.primary_type || 'other';
      primaryTypes[type] = (primaryTypes[type] || 0) + 1;
    });

    const primaryTypeDistribution = Object.entries(primaryTypes)
      .map(([type, count]) => ({
        name: type
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        value: ((count / reviewsData.length) * 100).toFixed(1),
        count
      }))
      .sort((a, b) => parseFloat(b.value) - parseFloat(a.value));

    // Prepare visualization data - use last 12 months
    const activityTrendData = monthlyTrends.slice(-12);
    
    // Calculate average activity for the trend line
    const activityAverage = activityTrendData.length > 0
      ? activityTrendData.reduce((sum, d) => sum + d.count, 0) / activityTrendData.length
      : 0;
    
    const competitorData = sortedByTotal.map((business) => ({
      name: business.name.split(' ').slice(0, 2).join(' '),
      reviews: business.rating_count,
      rating: business.rating_avg.toFixed(1)
    }));

    const sentimentOverview = {
      positive: sentimentData.reduce((sum, b) => sum + b.positive, 0),
      neutral: sentimentData.reduce((sum, b) => sum + b.neutral, 0),
      negative: sentimentData.reduce((sum, b) => sum + b.negative, 0)
    };
    const totalSentiment =
      sentimentOverview.positive +
      sentimentOverview.neutral +
      sentimentOverview.negative;
    const sentimentDistribution = {
      positive: ((sentimentOverview.positive / totalSentiment) * 100).toFixed(
        1
      ),
      neutral: ((sentimentOverview.neutral / totalSentiment) * 100).toFixed(1),
      negative: ((sentimentOverview.negative / totalSentiment) * 100).toFixed(1)
    };

    // Calculate recent trends (last month vs previous month)
    const recentTrends = reviewsData
      .map((business) => {
        const yearInfo = yearData.find(
          (yd) => yd.maps_uri === business.maps_uri
        );
        const monthlyData = yearInfo?.data?.monthlyReviewCounts || {};
        const months = Object.keys(monthlyData).sort();
        const lastMonth = monthlyData[months[months.length - 1]] || 0;
        const previousMonth = monthlyData[months[months.length - 2]] || 0;
        const trend =
          previousMonth > 0
            ? ((lastMonth - previousMonth) / previousMonth) * 100
            : 0;

        return {
          name: business.name,
          trend,
          lastMonth,
          previousMonth
        };
      })
      .sort((a, b) => b.trend - a.trend);

    // Create a preview version of the macro insight
    let macroInsightPreview = null;
    if (macroInsight) {
      const sentences = macroInsight.split('. ');
      const rawPreview =
        sentences.slice(0, 2).join('. ') + (sentences.length > 2 ? '...' : '.');
      macroInsightPreview = rawPreview
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/```(.*?)```/gs, '$1')
        .replace(/`(.*?)`/g, '$1')
        .replace(/#\s+/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Corrected regex for [text](url)
    }

    // Calculate 3-month relative growth for each business
    const businessGrowthRateTrends = reviewsData
      .map((business) => {
        const yearInfo = yearData.find(
          (yd) => yd.maps_uri === business.maps_uri
        );
        const monthlyData = yearInfo?.data?.monthlyReviewCounts || {};
        const months = Object.keys(monthlyData).sort(
          (a, b) => new Date(a) - new Date(b)
        );

        let threeMonthRelativeGrowth = 0;
        if (months.length >= 6) {
          const last3MonthsSum = months
            .slice(-3)
            .reduce((sum, m) => sum + (monthlyData[m] || 0), 0);
          const prev3MonthsSum = months
            .slice(-6, -3)
            .reduce((sum, m) => sum + (monthlyData[m] || 0), 0);

          if (prev3MonthsSum > 0) {
            threeMonthRelativeGrowth =
              ((last3MonthsSum - prev3MonthsSum) / prev3MonthsSum) * 100;
          } else if (last3MonthsSum > 0) {
            // prev3MonthsSum is 0
            threeMonthRelativeGrowth = 100.0; // Assign fixed 100% for new growth from zero base
          }
          // If both sums are 0, growth remains 0
        }
        return {
          name: business.name,
          growthRate: threeMonthRelativeGrowth
        };
      })
      .sort((a, b) => b.growthRate - a.growthRate);

    return {
      totalReviews,
      avgRating,
      marketMomentum,
      competitionScore,
      avgSentiment,
      marketLeaders,
      activityTrendData,
      activityAverage,
      competitorData,
      sentimentDistribution,
      businessCount: reviewsData.length,
      typeDistribution: typeDistribution.slice(0, 10),
      primaryTypeDistribution,
      recentTrends: recentTrends.slice(0, 8),
      sentimentData: sentimentData.slice(0, 10),
      macroInsight,
      macroInsightPreview,
      businessGrowthRateTrends // Added new metric
    };
  };

  const metrics = calculateMetrics();

  // Prepare data for new pie charts
  const marketShareData = useMemo(() => {
    if (!metrics?.marketLeaders) return [];
    // Ensure 'share' is a number and handle cases where it might be undefined or not a number
    const validLeaders = metrics.marketLeaders
      .map((leader) => ({
        name: leader.name,
        value: parseFloat(leader.share)
      }))
      .filter((leader) => !isNaN(leader.value) && leader.name);

    // Create an "Other" category if there are more than 5 leaders
    if (validLeaders.length > 5) {
      const top5 = validLeaders.slice(0, 4); // Take top 4
      const otherValue = validLeaders
        .slice(4)
        .reduce((acc, curr) => acc + curr.value, 0);
      return [
        ...top5,
        { name: 'Other', value: parseFloat(otherValue.toFixed(1)) }
      ];
    }
    return validLeaders;
  }, [metrics?.marketLeaders]);

  const businessTypeData = useMemo(() => {
    if (!metrics?.primaryTypeDistribution) return [];
    // Ensure 'value' is a number and handle cases where it might be undefined or not a number
    const validTypes = metrics.primaryTypeDistribution
      .map((type) => ({
        name: type.name,
        value: parseFloat(type.value)
      }))
      .filter((type_) => !isNaN(type_.value) && type_.name);

    // Create an "Other" category if there are more than 5 types
    if (validTypes.length > 5) {
      const top5 = validTypes.slice(0, 4); // Take top 4
      const otherValue = validTypes
        .slice(4)
        .reduce((acc, curr) => acc + curr.value, 0);
      return [
        ...top5,
        { name: 'Other', value: parseFloat(otherValue.toFixed(1)) }
      ];
    }
    return validTypes;
  }, [metrics?.primaryTypeDistribution]);

  const totalReviewsPieData = useMemo(() => {
    if (!metrics?.marketLeaders) return [];
    const validData = metrics.marketLeaders
      .map((leader) => ({
        name: leader.name,
        value: leader.reviews // Use total reviews count
      }))
      .filter((item) => !isNaN(item.value) && item.name && item.value > 0);

    if (validData.length === 0) return [];
    const sortedData = validData.sort((a, b) => b.value - a.value);

    if (sortedData.length > 5) {
      const topN = sortedData.slice(0, 4);
      const otherValue = sortedData
        .slice(4)
        .reduce((acc, curr) => acc + curr.value, 0);
      return [...topN, { name: 'Other', value: otherValue }];
    }
    return sortedData;
  }, [metrics?.marketLeaders]);

  const grossGrowthPieData = useMemo(() => {
    if (!metrics?.recentTrends) return [];
    const validData = metrics.recentTrends
      .map((trend) => ({
        name: trend.name,
        value: trend.lastMonth // Absolute review count in the last month
      }))
      .filter((item) => !isNaN(item.value) && item.name && item.value > 0);

    if (validData.length === 0) return [];
    const sortedData = validData.sort((a, b) => b.value - a.value);

    if (sortedData.length > 5) {
      const topN = sortedData.slice(0, 4);
      const otherValue = sortedData
        .slice(4)
        .reduce((acc, curr) => acc + curr.value, 0);
      return [...topN, { name: 'Other', value: otherValue }];
    }
    return sortedData;
  }, [metrics?.recentTrends]);

  const webTrafficPieData = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0) return [];
    const validData = reviewsData
      .filter(b => b.traffic_data?.metrics?.estimated_monthly_visitors > 0)
      .map(b => ({
        name: b.name,
        value: b.traffic_data.metrics.estimated_monthly_visitors
      }))
      .sort((a, b) => b.value - a.value);

    if (validData.length === 0) return [];

    if (validData.length > 5) {
      const topN = validData.slice(0, 4);
      const otherValue = validData
        .slice(4)
        .reduce((acc, curr) => acc + curr.value, 0);
      return [...topN, { name: 'Other', value: otherValue }];
    }
    return validData;
  }, [reviewsData]);

  const relativeGrowthPieData = useMemo(() => {
    if (!metrics?.businessGrowthRateTrends) return []; // Use new data source

    const positiveGrowthTrends = metrics.businessGrowthRateTrends
      .filter((item) => item.growthRate > 0) // Filter by new growthRate field
      .map((item) => ({
        name: item.name,
        value: parseFloat(item.growthRate.toFixed(1)) // Use growthRate
      }));

    if (positiveGrowthTrends.length === 0) return [];
    // Data is already sorted by growthRate descending from calculateMetrics

    if (positiveGrowthTrends.length > 5) {
      const topN = positiveGrowthTrends.slice(0, 4);
      const otherValue = positiveGrowthTrends
        .slice(4)
        .reduce((acc, curr) => acc + curr.value, 0);
      return [
        ...topN,
        { name: 'Other', value: parseFloat(otherValue.toFixed(1)) }
      ];
    }
    return positiveGrowthTrends;
  }, [metrics?.businessGrowthRateTrends]); // Update dependency

  // Localytics Ranking chart data - prepare data for bar chart showing rankings per business
  const localyticsChartData = useMemo(() => {
    if (!localyticsData?.rankingResults) return [];

    const totalBusinesses = localyticsData.totalBusinesses || localyticsData.rankingResults.length;

    return localyticsData.rankingResults
      .filter(result => result.localytics_score !== null && result.localytics_score !== undefined)
      .slice(0, 15) // Show top 15 businesses for better visualization
      .map(result => {
        // Find the actual business data with sentiment score calculation
        const businessData = sortedBySentiment.find(b => b.name === result.business_name) ||
                             reviewsData.find(b => b.name === result.business_name) || {};
        
        // Find combinedData entry which has historicalData
        const combinedBusinessData = combinedData.find(b => b.name === result.business_name) || {};

        const rankingColor = getRankingColor(result.localytics_score, totalBusinesses);

        // Get traffic history for this business to calculate SEO growth
        const businessMapsUri = businessData.maps_uri || combinedBusinessData.maps_uri;
        const businessTrafficData = businessMapsUri ? trafficHistory[businessMapsUri] : null;
        const seoScoreData = calculateSeoScoreGrowth(businessData, businessTrafficData);

        return {
          name: result.business_name.length > 15 ?
            result.business_name.substring(0, 15) + '...' :
            result.business_name,
          fullName: result.business_name,
          localyticsScore: result.localytics_score,
          fill: rankingColor, // Use 'fill' property for Cell components
          rankingColor: rankingColor,
          // Rank breakdown
          growthRank: result.rankings?.growth || 'N/A',
          reviewsRank: result.rankings?.total_reviews || 'N/A',
          trafficRank: result.rankings?.traffic || 'N/A',
          sentimentRank: result.rankings?.sentiment || 'N/A',
          // Real business data
          totalReviews: businessData.rating_count || 0,
          avgRating: businessData.rating_avg || 0,
          monthlyVisitors: businessData.traffic_data?.metrics?.estimated_monthly_visitors || 0,
          sentimentScore: businessData.sentimentScore || 0,
          // SEO Score with growth data (same calculation as WebTrafficDashboard)
          seoScore: seoScoreData.currentScore,
          seoScoreGrowth: seoScoreData.percentChange,
          seoScoreTrend: seoScoreData.trend,
          // Full business object for tooltip
          businessObject: {
            ...businessData,
            ...combinedBusinessData,
            maps_uri: businessMapsUri
          }
        };
      });
    // Already sorted by score (higher is better)
  }, [localyticsData, reviewsData, sortedBySentiment, combinedData, trafficHistory]);

  // Create name-based business image mapping for podium display
  const businessImagesByName = useMemo(() => {
    if (!reviewsData || !businessImages) return {};
    const nameToImage = {};
    reviewsData.forEach(business => {
      if (business.name && business.maps_uri && businessImages[business.maps_uri]) {
        nameToImage[business.name] = businessImages[business.maps_uri];
      }
    });
    return nameToImage;
  }, [reviewsData, businessImages]);

  // Get top 3 businesses for podium from localyticsChartData
  const top3Localytics = useMemo(() => {
    if (!localyticsChartData || localyticsChartData.length < 3) return [];
    return localyticsChartData.slice(0, 3);
  }, [localyticsChartData]);

  if (['pending', 'scraping', 'delayed', 'failed'].includes(onboardingStatus)) {
    return <OnboardingStatusPanel status={onboardingStatus} businesses={reviewsData || []} />;
  }

  if (isLoading || !metrics) {
    return (
      <AnimatedBox
        animation='fadeIn'
        duration={0.5}
        as={Flex}
        justify='center'
        align='center'
        h='400px'
      >
        <VStack>
          <Heading>Loading dashboard...</Heading>
          <Text>Preparing insights for your business market</Text>
        </VStack>
      </AnimatedBox>
    );
  }

  // Custom Markdown components for better styling
  const markdownComponents = {
    h2: ({ node, ...props }) => (
      <Heading
        as='h2'
        size='md'
        mb={4}
        color={textColor}
        {...props}
      />
    ),
    h3: ({ node, ...props }) => (
      <Heading
        as='h3'
        size='sm'
        mb={3}
        mt={5}
        color={textColor}
        {...props}
      />
    ),
    p: ({ node, ...props }) => (
      <Text
        mb={3}
        color={subtitleColor}
        {...props}
      />
    ),
    ul: ({ node, ...props }) => (
      <List
        spacing={2}
        styleType='disc'
        pl={5}
        mb={4}
        sx={{
          '& li': {
            color: subtitleColor
          }
        }}
        {...props}
      />
    ),
    strong: ({ node, ...props }) => (
      <Text
        as='span'
        fontWeight='bold'
        color={textColor}
        {...props}
      />
    )
  };

  // Define markdown components specifically for the insight modal
  const modalMarkdownComponents = {
    p: ({ node, ...props }) => (
      <Text
        mb={3}
        color='gray.700'
        lineHeight='tall'
        {...props}
      >
        <BusinessTextRenderer 
          businesses={combinedData} 
          onBusinessClick={handleBusinessClick}
        >
          {props.children}
        </BusinessTextRenderer>
      </Text>
    ),
    strong: ({ node, ...props }) => (
      <Text
        as='span'
        fontWeight='bold'
        color='blue.700'
        {...props}
      >
        <BusinessTextRenderer 
          businesses={combinedData} 
          onBusinessClick={handleBusinessClick}
        >
          {props.children}
        </BusinessTextRenderer>
      </Text>
    ),
    ul: ({ node, ...props }) => (
      <List
        spacing={1}
        styleType='disc'
        pl={5}
        mb={3}
        {...props}
      />
    ),
    li: ({ node, ...props }) => (
      // Ensure direct children of li that are text don't add extra margins from a <p>
      // ReactMarkdown might wrap bare text in <p> tags inside <li>
      <ListItem
        color='gray.700'
        sx={{ '& > p': { mb: '0.2em' } }}
        {...props}
      >
        <BusinessTextRenderer 
          businesses={combinedData} 
          onBusinessClick={handleBusinessClick}
        >
          {props.children}
        </BusinessTextRenderer>
      </ListItem>
    ),
    em: ({ node, ...props }) => (
      <Text
        as='span'
        fontStyle='italic'
        color='gray.600'
        {...props}
      >
        <BusinessTextRenderer 
          businesses={combinedData} 
          onBusinessClick={handleBusinessClick}
        >
          {props.children}
        </BusinessTextRenderer>
      </Text>
    )
    // If you had actual h2/h3 in your macroInsight markdown, you could define them here
    // h2: ({ node, ...props }) => (<Heading as="h2" size="md" color="blue.700" mt={4} mb={2} {...props} />),
  };

  // Stat card component
  const StatCard = ({
    title,
    value,
    change,
    icon,
    description,
    color,
    index = 0
  }) => (
    <AnimatedBox
      animation='fadeIn'
      delay={0.1 + index * 0.07}
      duration={0.5}
      as={Card}
      bg={bgColor}
      borderWidth='1px'
      borderColor={borderColor}
      borderRadius='lg'
      boxShadow='sm'
      overflow='hidden'
      h='100%'
      transition='all 0.2s'
      _hover={{
        borderColor: 'blue.200',
        shadow: 'sm',
        transform: 'translateY(-2px)'
      }}
    >
      <CardBody p={5}>
        <HStack
          spacing={4}
          align='flex-start'
        >
          <Flex
            w='40px'
            h='40px'
            bg={`${color}.100`}
            color={`${color}.500`}
            borderRadius='lg'
            justify='center'
            align='center'
            flexShrink={0}
          >
            <Icon
              as={icon}
              boxSize={5}
            />
          </Flex>
          <VStack
            align='start'
            spacing={0}
            flex='1'
          >
            <Text
              mb={0}
              fontSize='sm'
              color={subtitleColor}
            >
              {title}
            </Text>
            <HStack
              spacing={2}
              align='center'
              py={2}
            >
              <Text
                as='span'
                fontSize='2xl'
                fontWeight='bold'
                lineHeight='1.2'
              >
                {value}
              </Text>
              {change && (
                <Badge
                  colorScheme={change.value >= 0 ? 'green' : 'red'}
                  fontSize='xs'
                  borderRadius='full'
                  px={2}
                  py={0.5}
                  display='flex'
                  alignItems='center'
                  ml={2}
                >
                  <Box
                    as='span'
                    display='inline-flex'
                    alignItems='center'
                  >
                    <Icon
                      as={change.value >= 0 ? FaArrowUp : FaArrowDown}
                      boxSize={2}
                    />
                    <Box
                      as='span'
                      ml='2px'
                    >
                      {Math.abs(change.value).toFixed(1)}%
                    </Box>
                  </Box>
                </Badge>
              )}
            </HStack>
            {description && (
              <Text
                fontSize='xs'
                color={subtitleColor}
                mt={1}
              >
                {description}
              </Text>
            )}
          </VStack>
        </HStack>
      </CardBody>
    </AnimatedBox>
  );

  // Leader card component
  const LeaderCard = ({ rank, business, bgColor, borderColor }) => (
    <Card
      bg={bgColor}
      borderWidth='1px'
      borderColor={borderColor}
      borderRadius='lg'
      boxShadow='sm'
      overflow='hidden'
      transition='all 0.2s'
      _hover={{
        transform: 'translateY(-2px)',
        shadow: 'md',
        borderColor: 'blue.400'
      }}
      cursor='pointer'
      mb={2}
    >
      <CardBody
        py={5} // Changed from py={4}
        px={4}
      >
        <Flex
          justify='space-between'
          align='center'
        >
          <HStack spacing={3}>
            {rank <= 3 ? (
              <Avatar
                bg={
                  rank === 1
                    ? 'yellow.400'
                    : rank === 2
                    ? 'gray.300'
                    : 'orange.300'
                }
                icon={
                  <Icon
                    as={FaCrown}
                    fontSize='1.2rem'
                    color='white'
                  />
                }
                size='sm'
              />
            ) : (
              <Avatar
                bg='blue.100'
                size='sm'
                name={`#${rank}`}
                color='blue.600'
              />
            )}
            <Box>
              <Text
                as={'span'}
                fontWeight='medium'
                noOfLines={1}
                mb={1.5} // Changed from mb={1}
              >
                <BusinessTextRenderer 
                  businesses={combinedData} 
                  onBusinessClick={handleBusinessClick}
                >
                  {business.name}
                </BusinessTextRenderer>
              </Text>
              <HStack
                spacing={2}
                fontSize='xs'
                color={subtitleColor}
                align='center'
              >
                <Text as={'span'}>
                  {business.reviews.toLocaleString()} reviews
                </Text>
                <Text as={'span'}>•</Text>
                <HStack
                  spacing='1'
                  align='center'
                >
                  <Icon
                    as={FaStar}
                    color='yellow.400'
                  />
                  <Text as={'span'}>{business.rating}</Text>
                </HStack>
              </HStack>
            </Box>
          </HStack>
          <Badge
            colorScheme='blue'
            px={2}
            py={1}
            ml={2}
          >
            {business.share}%
          </Badge>
        </Flex>
      </CardBody>
    </Card>
  );




  // Function to handle business name clicks
  const handleBusinessClick = (business) => {
    // Find the business in combined data with full historical information
    const businessWithFullData = combinedData.find(b => b.maps_uri === business.maps_uri);
    if (businessWithFullData) {
      setSelectedBusiness(businessWithFullData);
      setIsBusinessModalOpen(true);
    }
  };

  // Render the homepage
  return (
    <>
      <Box
        maxW='none'
        ml={0}
        py={8}
        px={4}
        mb={20} // Add some margin bottom to prevent overlap with fixed button
        minH="calc(100vh - 100px)" // Ensure minimum height fills viewport
      >
        {/* Header Section with Title and Search */}
        <Flex 
          justify="space-between" 
          align="flex-start" 
          mb={8}
          flexWrap="wrap"
          gap={4}
        >
          <Box>
            <Heading 
              as="h1" 
              size="xl" 
              color={headingColor}
              fontWeight="bold"
              mb={1}
            >
              {brandName} Market Intelligence Dashboard
            </Heading>
            <Text color={subtitleColor} fontSize="md">
              Analyzing {reviewsData.length || 20} businesses in your local market
            </Text>
          </Box>
        </Flex>

        {/* 3-Tier View Switch - Compact & Colorful */}
        <Box mb={8}>
          <Flex justify="center" align="center">
            <AnimatedBox animation='fadeIn' animationKey='home-view-switch' duration={0.5}>
              <HStack
                spacing={{ base: 1, md: 2 }}
                align="center"
                justify="center"
                bg={switchBgColor}
                borderRadius="full"
                p={1}
              >
                {/* Analyze - Left */}
                <Box
                  as="button"
                  onClick={() => setActiveView('analyze')}
                  position="relative"
                  px={{ base: 3, md: 4 }}
                  py={{ base: 1.5, md: 2 }}
                  borderRadius="full"
                  bgGradient={activeView === 'analyze' 
                    ? 'linear(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'none'}
                  bg={activeView === 'analyze' ? undefined : 'transparent'}
                  color={activeView === 'analyze' ? 'white' : 'gray.500'}
                  fontWeight={activeView === 'analyze' ? 'semibold' : 'medium'}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  boxShadow={activeView === 'analyze' 
                    ? '0 4px 15px -3px rgba(102, 126, 234, 0.4)' 
                    : 'none'}
                  transition="all 0.3s ease"
                  _hover={{
                    bg: activeView === 'analyze' ? undefined : switchHoverBg,
                    color: activeView === 'analyze' ? 'white' : 'gray.700',
                  }}
                >
                  Analyze
                </Box>

                {/* Home - Middle (Default) - Permanently slightly bigger */}
                <Box
                  as="button"
                  onClick={() => setActiveView('home')}
                  position="relative"
                  px={{ base: 6, md: 8 }}
                  py={{ base: 2.5, md: 3 }}
                  borderRadius="full"
                  bgGradient={activeView === 'home' 
                    ? 'linear(135deg, #11998e 0%, #38ef7d 100%)' 
                    : 'none'}
                  bg={activeView === 'home' ? undefined : 'transparent'}
                  color={activeView === 'home' ? 'white' : 'gray.500'}
                  fontWeight="semibold"
                  fontSize={{ base: 'md', md: 'lg' }}
                  boxShadow={activeView === 'home' 
                    ? '0 4px 15px -3px rgba(56, 239, 125, 0.4)' 
                    : 'none'}
                  transition="all 0.3s ease"
                  _hover={{
                    bg: activeView === 'home' ? undefined : switchHoverBg,
                    color: activeView === 'home' ? 'white' : 'gray.700',
                  }}
                >
                  Home
                </Box>

                {/* Win - Right */}
                <Box
                  as="button"
                  onClick={() => setActiveView('beat')}
                  position="relative"
                  px={{ base: 3, md: 4 }}
                  py={{ base: 1.5, md: 2 }}
                  borderRadius="full"
                  bgGradient={activeView === 'beat' 
                    ? 'linear(135deg, #f093fb 0%, #f5576c 100%)' 
                    : 'none'}
                  bg={activeView === 'beat' ? undefined : 'transparent'}
                  color={activeView === 'beat' ? 'white' : 'gray.500'}
                  fontWeight={activeView === 'beat' ? 'semibold' : 'medium'}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  boxShadow={activeView === 'beat' 
                    ? '0 4px 15px -3px rgba(245, 87, 108, 0.4)' 
                    : 'none'}
                  transition="all 0.3s ease"
                  _hover={{
                    bg: activeView === 'beat' ? undefined : switchHoverBg,
                    color: activeView === 'beat' ? 'white' : 'gray.700',
                  }}
                >
                  Win
                </Box>
              </HStack>
            </AnimatedBox>
          </Flex>
        </Box>

        {/* AI Chatbot Input with Rotating Prompts - Below View Switch */}
        <Flex justify="center" mb={6} ref={urlInputRef}>
          <HStack
            w="100%"
            maxW="600px"
            bg={bgColor}
            borderRadius="full"
            border="1px solid"
            borderColor={borderColor}
            px={4}
            py={2}
            spacing={3}
            transition="all 0.2s"
            cursor="pointer"
            onClick={() => !chatInputValue && onOpenChat && onOpenChat(AI_CHAT_PROMPTS[currentPromptIndex])}
            _hover={{
              borderColor: 'purple.300',
              shadow: 'sm',
            }}
            _focusWithin={{
              borderColor: 'purple.400',
              shadow: '0 0 0 2px rgba(159, 122, 234, 0.2)',
            }}
            sx={urlInputHighlight ? {
              animation: `${silverOutlineGlowHome} 1s ease-in-out 3`,
              animationFillMode: 'forwards',
            } : {}}
          >
            <Flex
              w="32px"
              h="32px"
              borderRadius="lg"
              bgGradient="linear(to-br, purple.400, pink.400)"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon as={LuSparkles} color="white" boxSize={4} />
            </Flex>
            <Input
              variant="unstyled"
              placeholder={AI_CHAT_PROMPTS[currentPromptIndex]}
              value={chatInputValue}
              onChange={(e) => setChatInputValue(e.target.value)}
              onKeyDown={handleChatKeyDown}
              onClick={(e) => e.stopPropagation()}
              fontSize="md"
              color={textColor}
              _placeholder={{ 
                color: 'gray.400',
                transition: 'opacity 0.3s ease',
              }}
              _focus={{
                boxShadow: 'none',
                outline: 'none',
              }}
              flex={1}
            />
            <IconButton
              icon={<LuSend />}
              size="md"
              variant="ghost"
              colorScheme="purple"
              borderRadius="full"
              onClick={(e) => {
                e.stopPropagation();
                handleChatSubmit();
              }}
              aria-label="Ask AI"
              _hover={{
                bg: 'purple.50',
              }}
            />
          </HStack>
        </Flex>

        {/* ========== ANALYZE COMPETITORS VIEW ========== */}
        {activeView === 'analyze' && (
          <>
            {/* 1. Favorites Widget - 50% width top left (same as Home) */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
              <FavoritesWidget onBusinessClick={handleBusinessClick} />
              {/* Dashboard Link Bubbles - Analyze Section */}
              <DashboardLinkBubbles 
                variant="analyze"
                links={[
                  { label: `${brandName} Ranking`, path: '/dashboards/localytics-score' },
                  { label: 'Compare Businesses', path: '/compare-year' },
                  { label: 'Month in Review', path: '/year-in-review' },
                  { label: 'Pricing Analysis', path: '/dashboards/pricing' },
                  { label: 'Reading List', path: '/playbook/reading' },
                  { label: 'Review Frequency', path: '/dashboards/total-reviews' },
                  { label: 'Review Growth', path: '/dashboards/gross-growth' },
                  { label: 'Sentiment', path: '/dashboards/sentiment' },
                ]}
              />
            </SimpleGrid>

            {/* 2. Ranking Pie Charts Section */}
            <AnimatedBox animation='fadeIn' delay={0.4} duration={0.7} mb={8}>
              <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
                {/* Total Reviews Pie Chart Card */}
                <Card bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' boxShadow='sm' transition='all 0.2s ease-in-out' _hover={{ shadow: 'md', transform: 'translateY(-3px)' }} onClick={() => navigate('/dashboards/total-reviews')} cursor='pointer'>
                  <CardHeader pb={2}>
                    <Heading size='md' color={textColor} textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Total Reviews</Heading>
                  </CardHeader>
                  <CardBody>
                    {totalReviewsPieData.length > 0 ? (
                      <Box h={{ base: '250px', md: '280px' }}>
                        <ResponsiveContainer width='100%' height='100%'>
                          <PieChart>
                            <Pie data={totalReviewsPieData} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={70} labelLine={true} label={({ name }) => name} isAnimationActive={true} animationDuration={800}>
                              {totalReviewsPieData.map((entry, index) => (
                                <Cell key={`cell-total-reviews-analyze-${index}`} fill={COLORS.charts[index % COLORS.charts.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(value, name) => [value.toLocaleString() + ' Reviews', name]} />
                            <Legend content={<CustomPieLegend />} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Text color={subtitleColor}>No data available</Text>
                    )}
                  </CardBody>
                </Card>

                {/* Website Traffic Pie Chart Card */}
                <Card bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' boxShadow='sm' transition='all 0.2s ease-in-out' _hover={{ shadow: 'md', transform: 'translateY(-3px)' }} onClick={() => navigate('/dashboards/web-traffic')} cursor='pointer'>
                  <CardHeader pb={2}>
                    <Heading size='md' color={textColor} textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Website Traffic</Heading>
                  </CardHeader>
                  <CardBody>
                    {webTrafficPieData.length > 0 ? (
                      <Box h={{ base: '250px', md: '280px' }}>
                        <ResponsiveContainer width='100%' height='100%'>
                          <PieChart>
                            <Pie data={webTrafficPieData} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={70} labelLine={true} label={({ name }) => name} isAnimationActive={true} animationDuration={800}>
                              {webTrafficPieData.map((entry, index) => (
                                <Cell key={`cell-web-traffic-analyze-${index}`} fill={COLORS.charts[(index + 4) % COLORS.charts.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(value, name) => [value.toLocaleString() + ' Backlinks', name]} />
                            <Legend content={<CustomPieLegend />} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Text color={subtitleColor}>No data available</Text>
                    )}
                  </CardBody>
                </Card>

                {/* Gross Growth Pie Chart Card */}
                <Card bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' boxShadow='sm' transition='all 0.2s ease-in-out' _hover={{ shadow: 'md', transform: 'translateY(-3px)' }} onClick={() => navigate('/dashboards/gross-growth')} cursor='pointer'>
                  <CardHeader pb={2}>
                    <Heading size='md' color={textColor} textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Review Growth</Heading>
                  </CardHeader>
                  <CardBody>
                    {grossGrowthPieData.length > 0 ? (
                      <Box h={{ base: '250px', md: '280px' }}>
                        <ResponsiveContainer width='100%' height='100%'>
                          <PieChart>
                            <Pie data={grossGrowthPieData} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={70} labelLine={true} label={({ name }) => name} isAnimationActive={true} animationDuration={800}>
                              {grossGrowthPieData.map((entry, index) => (
                                <Cell key={`cell-gross-growth-analyze-${index}`} fill={COLORS.charts[(index + 2) % COLORS.charts.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(value, name) => [value.toLocaleString() + ' Reviews Last Month', name]} />
                            <Legend content={<CustomPieLegend />} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Text color={subtitleColor}>No data available</Text>
                    )}
                  </CardBody>
                </Card>

              </SimpleGrid>
            </AnimatedBox>

            {/* 3. Localytics Ranking Widget & Compare Business Widget - Side by Side */}
            <Flex mb={6} gap={6} direction={{ base: 'column', lg: 'row' }}>
              <Box flex="1" minW="0">
                <LocalyticsScoreWidget />
              </Box>
              <Box flex="1" minW="0">
                <CompareBusinessWidget />
              </Box>
            </Flex>

            {/* 4. Review Totals & Web Traffic Widgets - Side by Side */}
            <Flex mb={6} gap={6} direction={{ base: 'column', lg: 'row' }}>
              <Box flex="1" minW="0">
                <ReviewTotalsWidget />
              </Box>
              <Box flex="1" minW="0">
                <WebTrafficWidget />
              </Box>
            </Flex>

            {/* 5. Month in Review - Full Width */}
            <Box mb={6}>
              <AnalyzeMonthSnapshot />
            </Box>

            {/* 6. Pricing Analysis Widget */}
            <Box mb={6}>
              {pricingLoading ? (
                <Flex justify="center" align="center" minH="200px">
                  <Spinner size="lg" color="green.500" />
                </Flex>
              ) : pricingData ? (
                <Box>
                  {/* Charts Row */}
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                    {/* Price Tier Distribution */}
                    <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg">
                      <CardHeader pb={0} pt={3} px={4}>
                        <HStack>
                          <Icon as={FaChartPie} color="blue.500" boxSize={4} />
                          <Heading size="sm" color={textColor}>Price Tier Distribution</Heading>
                        </HStack>
                      </CardHeader>
                      <CardBody pt={2} pb={3}>
                        <Box h="200px">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pricingPieChartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={70}
                                label={({ name, value, percent }) => 
                                  `${name} (${value} - ${(percent * 100).toFixed(0)}%)`
                                }
                                labelLine={true}
                              >
                                {pricingPieChartData.map((entry, index) => (
                                  <Cell key={`cell-pricing-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <RechartsTooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </CardBody>
                    </Card>

                    {/* Rating vs Price Scatter */}
                    <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg">
                      <CardHeader pb={0} pt={3} px={4}>
                        <HStack>
                          <Icon as={FaBalanceScale} color="purple.500" boxSize={4} />
                          <Heading size="sm" color={textColor}>Rating vs Price Level</Heading>
                        </HStack>
                      </CardHeader>
                      <CardBody pt={2} pb={3}>
                        <Box h="200px">
                          <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                              <XAxis 
                                type="number" 
                                dataKey="price_level" 
                                name="Price Level" 
                                domain={[0.5, 4.5]}
                                ticks={[1, 2, 3, 4]}
                                tickFormatter={(v) => '$'.repeat(v)}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis 
                                type="number" 
                                dataKey="rating" 
                                name="Rating" 
                                domain={[3, 5]}
                                tick={{ fontSize: 12 }}
                              />
                              <ZAxis type="number" dataKey="reviews" range={[40, 200]} />
                              <RechartsTooltip
                                cursor={{ strokeDasharray: '3 3' }}
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <Box bg={bgColor} p={2} borderRadius="md" shadow="md" borderWidth="1px" fontSize="sm">
                                        <Text fontWeight="bold">{data.name}</Text>
                                        <Text>Price: {'$'.repeat(data.price_level)}</Text>
                                        <Text>Rating: {data.rating.toFixed(1)} ⭐</Text>
                                      </Box>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Scatter 
                                data={pricingScatterData} 
                                fill="#805AD5"
                                fillOpacity={0.7}
                              />
                            </ScatterChart>
                          </ResponsiveContainer>
                        </Box>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* Tier Summary Cards */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mb={6}>
                    {Object.entries(PRICE_TIERS).map(([level, tier]) => {
                      const count = pricingData?.market_summary?.tier_distribution?.[tier.label] || 0;
                      return (
                        <Card 
                          key={level}
                          bg={bgColor}
                          borderWidth="1px"
                          borderColor={borderColor}
                          borderRadius="lg"
                        >
                          <CardBody textAlign="center" py={4} px={3}>
                            <Text fontSize="2xl" fontWeight="bold" color={`${tier.color}.500`} mb={1}>
                              {tier.label}
                            </Text>
                            <Text fontSize="xs" color={subtitleColor} mb={3}>
                              {tier.description}
                            </Text>
                            <Text fontSize="xl" fontWeight="bold" color={textColor}>
                              {count}
                            </Text>
                            <Text fontSize="xs" color={`${tier.color}.500`}>
                              businesses
                            </Text>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </SimpleGrid>

                  {/* Filters */}
                  <Flex gap={3} flexWrap="wrap" align="center">
                    <InputGroup maxW="220px" size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={FaSearch} color="gray.400" boxSize={3} />
                      </InputLeftElement>
                      <Input
                        placeholder="Search businesses..."
                        value={pricingSearchQuery}
                        onChange={(e) => setPricingSearchQuery(e.target.value)}
                        bg={bgColor}
                        borderColor={borderColor}
                        borderRadius="md"
                      />
                    </InputGroup>
                    
                    <HStack spacing={2}>
                      <Icon as={FaFilter} color="gray.400" boxSize={3} />
                      <Select
                        value={pricingFilterTier}
                        onChange={(e) => setPricingFilterTier(e.target.value)}
                        maxW="120px"
                        size="sm"
                        bg={bgColor}
                        borderColor={borderColor}
                      >
                        <option value="all">All Tiers</option>
                        <option value="1">$</option>
                        <option value="2">$$</option>
                        <option value="3">$$$</option>
                        <option value="4">$$$$</option>
                      </Select>
                    </HStack>

                    <HStack spacing={2}>
                      <Icon as={FaSortAmountDown} color="gray.400" boxSize={3} />
                      <Select
                        value={pricingSortBy}
                        onChange={(e) => setPricingSortBy(e.target.value)}
                        maxW="160px"
                        size="sm"
                        bg={bgColor}
                        borderColor={borderColor}
                      >
                        <option value="price_level">Price: Low to High</option>
                        <option value="price_level_desc">Price: High to Low</option>
                        <option value="rating">Highest Rated</option>
                        <option value="name">Name A-Z</option>
                      </Select>
                    </HStack>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/dashboards/pricing')}
                    >
                      Expand All
                    </Button>

                    <Text fontSize="sm" color={subtitleColor} ml="auto">
                      Showing {filteredPricingBusinesses.length} of {pricingData?.businesses?.length || 0} businesses
                    </Text>
                  </Flex>
                </Box>
              ) : (
                <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
                  <Text color={subtitleColor} textAlign="center">
                    Unable to load pricing data. <Button variant="link" colorScheme="green" onClick={() => navigate('/dashboards/pricing')}>View full dashboard</Button>
                  </Text>
                </Card>
              )}
            </Box>

            {/* 7. Sentiment Analysis Widget - Full Width */}
            <Box mb={6}>
              <SentimentAnalysisWidget />
            </Box>

            {/* 8. Last Month's Leaders - Full Width (from Home) */}
            <Box mb={6}>
              <LastMonthLeaders yearData={yearData} onBusinessClick={handleBusinessClick} />
            </Box>

            {/* 9. Bottom Section - Market Leaders, Growth Trends, Business Types (from Home) */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} mb={10}>
              {/* Market Leaders */}
              <AnimatedBox animation='fadeIn' delay={0.2} duration={0.6} as={Card} bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' overflow='hidden' boxShadow='sm' _hover={{ shadow: 'md', borderColor: 'blue.200' }} transition='all 0.3s' h='100%'>
                <CardHeader pb={0}>
                  <Flex justify='space-between' align='center'>
                    <Box as='span' display='inline-flex' alignItems='center'>
                      <Icon as={FaTrophy} color='yellow.400' mr={2} />
                      <Heading as='span' size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Market Leaders</Heading>
                    </Box>
                    <Button size='sm' colorScheme='blue' variant='ghost' onClick={() => navigate('/dashboards/total-reviews')}>Rankings</Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={6}>
                  <VStack spacing={1} align='stretch'>
                    {metrics.marketLeaders.slice(0, 5).map((leader, index) => (
                      <LeaderCard key={index} rank={index + 1} business={leader} bgColor={bgColor} borderColor={borderColor} />
                    ))}
                  </VStack>
                </CardBody>
              </AnimatedBox>

              {/* Recent Trends */}
              <AnimatedBox animation='fadeIn' delay={0.3} duration={0.6} as={Card} bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' overflow='hidden' boxShadow='sm' _hover={{ shadow: 'md', borderColor: 'blue.200' }} transition='all 0.3s' h='100%'>
                <CardHeader pb={0}>
                  <Flex justify='space-between' align='center'>
                    <Box as='span' display='inline-flex' alignItems='center'>
                      <Icon as={FaChartLine} color='blue.500' mr={2} />
                      <Heading as='span' size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Growth Trends</Heading>
                    </Box>
                    <Button size='sm' colorScheme='blue' variant='ghost' onClick={() => navigate('/dashboards/gross-growth')}>Details</Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={6}>
                  <VStack spacing={1} align='stretch'>
                    {metrics.recentTrends.slice(0, 5).map((trend, index) => (
                      <Card bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' boxShadow='sm' overflow='hidden' transition='all 0.2s' _hover={{ transform: 'translateY(-2px)', shadow: 'md' }} cursor='pointer' mb={2} key={index}>
                        <CardBody py={5} px={4}>
                          <Flex justify='space-between' align='center'>
                            <VStack justify='start' align='start' spacing={1.5}>
                              <Text as='span' fontWeight='medium' noOfLines={1}>{trend.name}</Text>
                              <Text mb={0} fontSize='xs' color={subtitleColor}>{trend.lastMonth} reviews last month (vs {trend.previousMonth})</Text>
                            </VStack>
                            <Badge colorScheme={trend.trend >= 0 ? 'green' : 'red'} variant='subtle' px={2} py={0.5} borderRadius='full'>
                              <Icon as={trend.trend >= 0 ? FaArrowUp : FaArrowDown} boxSize={2.5} />
                              <Box as='span' ml='2px'>{Math.abs(trend.trend).toFixed(1)}%</Box>
                            </Badge>
                          </Flex>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                </CardBody>
              </AnimatedBox>

              {/* Business Types */}
              <AnimatedBox animation='fadeIn' delay={0.4} duration={0.6} as={Card} bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' overflow='hidden' boxShadow='sm' _hover={{ shadow: 'md', borderColor: 'blue.200' }} transition='all 0.3s' h='100%'>
                <CardHeader pb={0}>
                  <Flex justify='space-between' align='center'>
                    <Box as='span' display='inline-flex' alignItems='center'>
                      <Icon as={FaStoreAlt} color='blue.500' mr={2} />
                      <Heading as='span' size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Business Types</Heading>
                    </Box>
                    <Button size='sm' colorScheme='blue' variant='ghost' onClick={() => navigate('/dashboards/business-insights')}>Explore</Button>
                  </Flex>
                </CardHeader>
                <CardBody pt={6}>
                  <VStack spacing={1} align='stretch'>
                    {metrics.primaryTypeDistribution.slice(0, 5).map((category, index) => (
                      <Card bg={bgColor} borderWidth='1px' borderColor={borderColor} borderRadius='lg' boxShadow='sm' overflow='hidden' transition='all 0.2s' _hover={{ transform: 'translateY(-2px)', shadow: 'md' }} cursor='pointer' mb={2} key={category.name}>
                        <CardBody py={5} px={4} minH='66px'>
                          <VStack spacing={2} align='stretch'>
                            <Flex justify='space-between' align='center'>
                              <Text as='span' fontWeight='medium' noOfLines={1}>{category.name}</Text>
                              <Text fontSize='sm' fontWeight='bold' as='span'>{category.value}%</Text>
                            </Flex>
                            <Box w='100%' h='8px' bg='gray.100' borderRadius='full' overflow='hidden'>
                              <Box w={`${category.value}%`} h='100%' bg={COLORS.charts[index % COLORS.charts.length]} borderRadius='full' />
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                </CardBody>
              </AnimatedBox>
            </SimpleGrid>

            {/* 10. Review Frequency Over Time - Full Width with Business Selection */}
            <AnimatedBox
              animation='fadeIn'
              delay={0.2}
              duration={0.7}
              as={Card}
              bg={bgColor}
              borderWidth='1px'
              borderColor={borderColor}
              borderRadius='lg'
              overflow='hidden'
              boxShadow='sm'
              mb={10}
              _hover={{
                shadow: 'md',
                borderColor: 'purple.200'
              }}
              transition='all 0.3s'
            >
              <CardHeader pb={2}>
                <Flex justify='space-between' align='center' wrap='wrap' gap={4}>
                  <HStack spacing={3}>
                    <Icon as={FaChartLine} color='purple.500' boxSize={5} />
                    <Heading size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
                      Review Frequency Over Time
                    </Heading>
                  </HStack>
                  <HStack spacing={2}>
                    <Badge colorScheme='purple' px={3} py={1} borderRadius='full'>
                      {yearData.length} Businesses
                    </Badge>
                    <Button size='sm' colorScheme='purple' variant='ghost' onClick={() => navigate('/dashboards/total-reviews')}>
                      View Details
                    </Button>
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody>
                {(() => {
                  const businessList = yearData
                    .filter(b => b?.data?.monthlyReviewCounts && Object.keys(b.data.monthlyReviewCounts).length > 0)
                    .map(b => ({
                      name: b.name,
                      shortName: b.name?.split(' ').slice(0, 2).join(' ') || 'Unknown',
                      maps_uri: b.maps_uri,
                      totalReviews: Object.values(b.data?.monthlyReviewCounts || {}).reduce((a, c) => a + c, 0)
                    }))
                    .sort((a, b) => b.totalReviews - a.totalReviews);

                  const lineColors = [
                    '#9B51E0', '#2F80ED', '#27AE60', '#EB5757', '#F2994A',
                    '#219EBC', '#F264A6', '#BB6BD9', '#2D9CDB', '#219653',
                    '#F2C94C', '#6FCF97', '#56CCF2', '#E91E63', '#00BCD4',
                    '#FF5722', '#607D8B', '#795548', '#9C27B0', '#3F51B5'
                  ];

                  const allMonths = new Set();
                  yearData.forEach(business => {
                    Object.keys(business?.data?.monthlyReviewCounts || {}).forEach(m => allMonths.add(m));
                  });
                  const sortedMonths = Array.from(allMonths).sort().slice(-12);

                  const chartData = sortedMonths.map(month => {
                    const point = {
                      month,
                      displayMonth: new Date(month + '-01T00:00:00').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                    };
                    yearData.forEach(business => {
                      const shortName = business.name?.split(' ').slice(0, 2).join(' ') || 'Unknown';
                      point[shortName] = business?.data?.monthlyReviewCounts?.[month] || 0;
                    });
                    return point;
                  });

                  return (
                    <VStack spacing={4} align='stretch'>
                      <Box>
                        <Text fontSize='sm' fontWeight='semibold' color={subtitleColor} mb={2}>
                          Select businesses to display (click to toggle):
                        </Text>
                        <Flex wrap='wrap' gap={2}>
                          {businessList.map((business, index) => {
                            const isSelected = !analyzeSelectedReviewBusinesses.includes(business.shortName);
                            return (
                              <Badge
                                key={business.maps_uri || index}
                                px={3}
                                py={1}
                                borderRadius='full'
                                cursor='pointer'
                                bg={isSelected ? lineColors[index % lineColors.length] : 'gray.200'}
                                color={isSelected ? 'white' : 'gray.500'}
                                opacity={isSelected ? 1 : 0.6}
                                _hover={{ opacity: 0.8, transform: 'scale(1.05)' }}
                                transition='all 0.2s'
                                onClick={() => {
                                  setAnalyzeSelectedReviewBusinesses(prev => 
                                    prev.includes(business.shortName)
                                      ? prev.filter(n => n !== business.shortName)
                                      : [...prev, business.shortName]
                                  );
                                }}
                              >
                                {business.shortName}
                              </Badge>
                            );
                          })}
                        </Flex>
                        <HStack mt={2} spacing={2}>
                          <Button size='xs' variant='outline' colorScheme='purple' onClick={() => setAnalyzeSelectedReviewBusinesses([])}>
                            Select All
                          </Button>
                          <Button size='xs' variant='outline' colorScheme='gray' onClick={() => setAnalyzeSelectedReviewBusinesses(businessList.map(b => b.shortName))}>
                            Deselect All
                          </Button>
                        </HStack>
                      </Box>
                      <Box h='400px'>
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 25 }}>
                            <CartesianGrid strokeDasharray='3 3' stroke={borderColor} />
                            <XAxis dataKey='displayMonth' tick={{ fontSize: 11, fill: subtitleColor }} />
                            <YAxis tick={{ fontSize: 11, fill: subtitleColor }} width={40} />
                            <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
                            {businessList.map((business, index) => {
                              const isHidden = analyzeSelectedReviewBusinesses.includes(business.shortName);
                              if (isHidden) return null;
                              return (
                                <Line
                                  key={business.shortName}
                                  type='monotone'
                                  dataKey={business.shortName}
                                  stroke={lineColors[index % lineColors.length]}
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  activeDot={{ r: 5 }}
                                />
                              );
                            })}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </VStack>
                  );
                })()}
              </CardBody>
            </AnimatedBox>
          </>
        )}


        {/* ========== BEAT COMPETITORS VIEW ========== */}
        {activeView === 'beat' && (
          <>
            {/* 1. Favorites Widget - 50% width top left (same as Home) */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
              <FavoritesWidget onBusinessClick={handleBusinessClick} />
              {/* Dashboard Link Bubbles - Win Section */}
              <DashboardLinkBubbles 
                variant="win"
                links={[
                  { label: 'Actions', path: '/playbook/actions' },
                  { label: 'Set Goals', path: '/playbook/goals' },
                  { label: 'Google Ads', path: '/dashboards/ads-intelligence' },
                  { label: 'Content', path: '/dashboards/content-templates' },
                  { label: 'Keywords', path: '/dashboards/keyword-intelligence' },
                ]}
              />
            </SimpleGrid>

            {/* 2. Set Goals - Full Width */}
            <Box mb={10}>
              <GoalsWidget />
            </Box>

            {/* 3. Actions - Full Width */}
            <Box mb={6}>
              <ActionsPlaybookSnippet />
            </Box>

            {/* 4. Advertising Intel - Full Width */}
            <Box mb={6}>
              <AdIntelligenceWidget variant="full" />
            </Box>

            {/* 5. Content Templates - Full Width */}
            <Box mb={6}>
              <ContentTemplatesWidget reviewsData={reviewsData} />
            </Box>

            {/* 6. Keywords Intelligence - Full Width */}
            <Box mb={6}>
              <KeywordIntelligenceWidget />
            </Box>
          </>
        )}

        {/* ========== HOME VIEW (Default) ========== */}
        {activeView === 'home' && (
          <>
            {/* Favorites & Market Demand Widgets - Side by Side */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
              <FavoritesWidget onBusinessClick={handleBusinessClick} />
              <MarketDemandWidget />
            </SimpleGrid>
          </>
        )}

        {/* Who's Making Money & Slow/Busy Months Widgets - Side by Side (Home view only) */}
        {activeView === 'home' && (
          <>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
          {/* Localytics Rankings Chart */}
          <AnimatedBox
            animation='fadeIn'
            animationKey='home-localytics-scores'
            delay={0.4}
            duration={0.7}
            as={Card}
            bg={bgColor}
            borderWidth='1px'
            borderColor={borderColor}
            borderRadius='lg'
            overflow='visible'
            boxShadow='sm'
            _hover={{
              shadow: 'md',
              borderColor: 'blue.200'
            }}
            transition='all 0.3s'
          >
            <CardHeader pb={0}>
              <Flex
                justify='space-between'
                align='center'
              >
                <Box>
                  <Heading size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
                    {localyticsLoading ? 'Loading Rankings...' : 'Demand'}
                  </Heading>
                </Box>
                <Button
                  size='sm'
                  colorScheme='blue'
                  variant='ghost'
                  onClick={() => navigate('/dashboards/localytics-score')}
                  isDisabled={localyticsLoading}
                >
                  View Details
                </Button>
              </Flex>
            </CardHeader>
            <CardBody overflow="visible">
              <Box h='400px' overflow="visible" position="relative">
                {localyticsLoading ? (
                  <Flex
                    justify='center'
                    align='center'
                    h='100%'
                  >
                    <VStack>
                      <Text color={subtitleColor}>Calculating {brandName} Rankings...</Text>
                      <Text fontSize='sm' color={subtitleColor}>
                        Ranking {metrics?.businessCount || 0} businesses across performance categories
                      </Text>
                      <Text fontSize='xs' color={subtitleColor}>
                        Growth • Reviews • Traffic • Sentiment
                      </Text>
                    </VStack>
                  </Flex>
                ) : localyticsChartData.length > 0 ? (
                  <Flex direction="column" h="100%" justify="flex-end">
                    {/* Compact Podium - Top 3 - Positioned above the chart */}
                    {top3Localytics.length >= 3 && (
                      <Flex 
                        justify="center" 
                        align="flex-end" 
                        gap={3} 
                        pt={1}
                        pb={2}
                        mb={-2}
                      >
                        {/* 2nd Place */}
                        <VStack spacing={0} align="center" w="90px">
                          <Tooltip label={top3Localytics[1]?.fullName} placement="top" hasArrow>
                            <VStack spacing={0}>
                              <Avatar 
                                name={top3Localytics[1]?.fullName} 
                                src={businessImagesByName[top3Localytics[1]?.fullName] || ''} 
                                size="sm"
                                bg="gray.100"
                                borderWidth="2px"
                                borderColor="gray.300"
                              />
                              <Text fontSize="2xs" fontWeight="medium" color={textColor} noOfLines={1} textAlign="center" maxW="85px">
                                {top3Localytics[1]?.fullName?.split(' ')[0]}
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color="gray.600">
                                {formatRanking(top3Localytics[1]?.localyticsScore)}
                              </Text>
                            </VStack>
                          </Tooltip>
                          <Box 
                            w="70px" 
                            h="32px" 
                            bg="gray.200" 
                            borderTopRadius="md"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="md" fontWeight="bold" color="gray.500">2</Text>
                          </Box>
                        </VStack>

                        {/* 1st Place - Center & Tallest */}
                        <VStack spacing={0} align="center" w="100px">
                          <Tooltip label={top3Localytics[0]?.fullName} placement="top" hasArrow>
                            <VStack spacing={0}>
                              <Icon as={FaTrophy} color="yellow.500" boxSize={4} mb={0.5} />
                              <Avatar 
                                name={top3Localytics[0]?.fullName} 
                                src={businessImagesByName[top3Localytics[0]?.fullName] || ''} 
                                size="md"
                                bg="yellow.50"
                                borderWidth="2px"
                                borderColor="yellow.400"
                              />
                              <Text fontSize="2xs" fontWeight="semibold" color={textColor} noOfLines={1} textAlign="center" maxW="95px">
                                {top3Localytics[0]?.fullName?.split(' ')[0]}
                              </Text>
                              <Text fontSize="md" fontWeight="bold" bgGradient="linear(to-r, blue.500, blue.600)" bgClip="text">
                                {formatRanking(top3Localytics[0]?.localyticsScore)}
                              </Text>
                            </VStack>
                          </Tooltip>
                          <Box 
                            w="75px" 
                            h="44px" 
                            bgGradient="linear(to-t, yellow.200, yellow.100)" 
                            borderTopRadius="md"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="lg" fontWeight="bold" color="yellow.600">1</Text>
                          </Box>
                        </VStack>

                        {/* 3rd Place */}
                        <VStack spacing={0} align="center" w="90px">
                          <Tooltip label={top3Localytics[2]?.fullName} placement="top" hasArrow>
                            <VStack spacing={0}>
                              <Avatar 
                                name={top3Localytics[2]?.fullName} 
                                src={businessImagesByName[top3Localytics[2]?.fullName] || ''} 
                                size="sm"
                                bg="orange.50"
                                borderWidth="2px"
                                borderColor="orange.300"
                              />
                              <Text fontSize="2xs" fontWeight="medium" color={textColor} noOfLines={1} textAlign="center" maxW="85px">
                                {top3Localytics[2]?.fullName?.split(' ')[0]}
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color="orange.500">
                                {formatRanking(top3Localytics[2]?.localyticsScore)}
                              </Text>
                            </VStack>
                          </Tooltip>
                          <Box 
                            w="70px" 
                            h="24px" 
                            bg="orange.100" 
                            borderTopRadius="md"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="sm" fontWeight="bold" color="orange.500">3</Text>
                          </Box>
                        </VStack>
                      </Flex>
                    )}

                    {/* Bar Chart - Fixed at bottom, 50% larger */}
                    <Box h="280px">
                      <ResponsiveContainer width='100%' height='100%'>
                        <BarChart
                          data={localyticsChartData}
                          margin={{ top: 20, right: 20, left: 60, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray='3 3' stroke={borderColor} />
                          <XAxis
                            dataKey='name'
                            tick={{ fontSize: 11, fill: subtitleColor, fontWeight: 600 }}
                            angle={-45}
                            textAnchor='end'
                            height={80}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 9, fill: subtitleColor }}
                            domain={[0, 100]}
                            tickFormatter={(value) => value.toString()}
                            width={30}
                            label={{ value: 'Score', angle: -90, position: 'insideLeft', fontSize: 8 }}
                          />
                          <RechartsTooltip
                            formatter={(value, name) => [
                              formatRanking(value),
                              `${brandName} Ranking`
                            ]}
                            labelFormatter={(label) => {
                              const item = localyticsChartData.find(d => d.name === label);
                              return item?.fullName || label;
                            }}
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <BusinessInsightTooltipCard
                                    data={data}
                                    businessImages={businessImages}
                                    trafficHistory={trafficHistory}
                                    bgColor={bgColor}
                                    borderColor={borderColor}
                                  />
                                );
                              }
                              return null;
                            }}
                            offset={20}
                            position={{ y: -350 }}
                            wrapperStyle={{ 
                              zIndex: 1000,
                              pointerEvents: 'none',
                              overflow: 'visible'
                            }}
                            allowEscapeViewBox={{ x: true, y: true }}
                          />
                          <Bar
                            dataKey='localyticsScore'
                            radius={[4, 4, 0, 0]}
                            animationDuration={1000}
                            onClick={() => navigate('/dashboards/localytics-score')}
                            style={{ cursor: 'pointer' }}
                          >
                            {localyticsChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} style={{ cursor: 'pointer' }} />
                            ))}
                            <LabelList
                              dataKey='localyticsScore'
                              position='top'
                              formatter={(value) => Math.round(value)}
                              style={{ fontSize: '9px', fontWeight: 'bold', fill: '#4A5568' }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Flex>
                ) : (
                  <Flex
                    justify='center'
                    align='center'
                    h='100%'
                  >
                    <VStack>
                      <Text color={subtitleColor}>{brandName} Rankings not available</Text>
                      <Text fontSize='sm' color={subtitleColor}>
                        Analyzing business performance metrics...
                      </Text>
                    </VStack>
                  </Flex>
                )}
              </Box>
            </CardBody>
          </AnimatedBox>
          {/* Market Activity Chart - Seasonal Version */}
          <AnimatedBox
            animation='fadeIn'
            animationKey='home-market-activity'
            delay={0.2}
            duration={0.7}
            as={Card}
            bg={bgColor}
            borderWidth='1px'
            borderColor={borderColor}
            borderRadius='lg'
            overflow='hidden'
            boxShadow='sm'
            _hover={{
              shadow: 'md',
              borderColor: 'blue.200'
            }}
            transition='all 0.3s'
          >
            <CardHeader pb={0}>
              <Flex
                justify='space-between'
                align='center'
              >
                <Heading size='md' textTransform="uppercase" fontStyle="italic" fontSize="1.3em">Seasonality</Heading>
                <Button
                  size='sm'
                  colorScheme='blue'
                  variant='ghost'
                  onClick={() => navigate('/dashboards/total-reviews')}
                >
                  View Details
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
              <Box h='300px'>
                <ResponsiveContainer
                  width='100%'
                  height='100%'
                >
                  {(() => {
                    const data = metrics.activityTrendData || [];
                    const avg = metrics.activityAverage || 0;
                    const maxCount = Math.max(...data.map(d => d.count), avg * 1.1);
                    
                    // Season colors - soft but lively for fill, vibrant for line
                    const seasonColors = {
                      winter: '#6BA3D4', // Soft sky blue
                      spring: '#E8A4BC', // Soft rose pink
                      summer: '#F2C86C', // Warm golden yellow
                      fall: '#C49A8C'    // Warm terracotta
                    };
                    const seasonLineColors = {
                      winter: '#3B8FCC', // Bright blue
                      spring: '#D65A8C', // Bright rose
                      summer: '#E5A41A', // Bright gold
                      fall: '#9E6B5A'    // Rich warm brown
                    };

                    // Helper function to get season info based on month
                    const getSeasonInfo = (monthStr) => {
                      const monthMap = {
                        'Jan': { season: 'Winter', emoji: '❄️', color: seasonColors.winter, lineColor: seasonLineColors.winter },
                        'Feb': { season: 'Winter', emoji: '❄️', color: seasonColors.winter, lineColor: seasonLineColors.winter },
                        'Mar': { season: 'Spring', emoji: '🌸', color: seasonColors.spring, lineColor: seasonLineColors.spring },
                        'Apr': { season: 'Spring', emoji: '🌸', color: seasonColors.spring, lineColor: seasonLineColors.spring },
                        'May': { season: 'Spring', emoji: '🌸', color: seasonColors.spring, lineColor: seasonLineColors.spring },
                        'Jun': { season: 'Summer', emoji: '☀️', color: seasonColors.summer, lineColor: seasonLineColors.summer },
                        'Jul': { season: 'Summer', emoji: '☀️', color: seasonColors.summer, lineColor: seasonLineColors.summer },
                        'Aug': { season: 'Summer', emoji: '☀️', color: seasonColors.summer, lineColor: seasonLineColors.summer },
                        'Sep': { season: 'Fall', emoji: '🍂', color: seasonColors.fall, lineColor: seasonLineColors.fall },
                        'Oct': { season: 'Fall', emoji: '🍂', color: seasonColors.fall, lineColor: seasonLineColors.fall },
                        'Nov': { season: 'Fall', emoji: '🍂', color: seasonColors.fall, lineColor: seasonLineColors.fall },
                        'Dec': { season: 'Winter', emoji: '❄️', color: seasonColors.winter, lineColor: seasonLineColors.winter }
                      };
                      return monthMap[monthStr] || { season: 'Unknown', emoji: '📅', color: '#999', lineColor: '#666' };
                    };

                    // Enhance data with season info
                    const enhancedData = data.map(item => ({
                      ...item,
                      ...getSeasonInfo(item.month),
                      percentFromAvg: avg > 0 ? ((item.count - avg) / avg * 100) : 0
                    }));
                    
                    return (
                      <AreaChart
                        data={enhancedData}
                        margin={{ top: 10, right: 50, left: 0, bottom: 25 }}
                      >
                        <defs>
                          {/* Horizontal gradient for seasonal color transition */}
                          <linearGradient
                            id='seasonalGradient'
                            x1='0'
                            y1='0'
                            x2='1'
                            y2='0'
                          >
                            {enhancedData.map((item, index) => {
                              const percent = (index / (enhancedData.length - 1)) * 100;
                              return (
                                <stop
                                  key={index}
                                  offset={`${percent}%`}
                                  stopColor={item.color}
                                  stopOpacity={0.6}
                                />
                              );
                            })}
                          </linearGradient>
                          <linearGradient
                            id='seasonalLineGradient'
                            x1='0'
                            y1='0'
                            x2='1'
                            y2='0'
                          >
                            {enhancedData.map((item, index) => {
                              const percent = (index / (enhancedData.length - 1)) * 100;
                              return (
                                <stop
                                  key={index}
                                  offset={`${percent}%`}
                                  stopColor={item.lineColor}
                                  stopOpacity={1}
                                />
                              );
                            })}
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray='3 3'
                          vertical={false}
                          stroke='#eee'
                        />
                        <XAxis
                          dataKey='month'
                          axisLine={false}
                          tickLine={false}
                          tick={(props) => {
                            const { x, y, payload, index } = props;
                            const item = enhancedData[index];
                            return (
                              <g transform={`translate(${x},${y})`}>
                                <text
                                  x={0}
                                  y={0}
                                  dy={14}
                                  textAnchor='middle'
                                  fill={item?.lineColor || '#666'}
                                  fontSize={11}
                                  fontWeight='500'
                                >
                                  {payload.value}
                                </text>
                                <text
                                  x={0}
                                  y={0}
                                  dy={28}
                                  textAnchor='middle'
                                  fill='#999'
                                  fontSize={10}
                                >
                                  {item?.year ? `'${item.year}` : ''}
                                </text>
                              </g>
                            );
                          }}
                          height={45}
                          interval={0}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tickCount={5}
                          domain={[0, maxCount]}
                        />
                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              const item = payload[0].payload;
                              const percentFromAvg = item.percentFromAvg;
                              const isAboveAvg = percentFromAvg >= 0;
                              return (
                                <Box
                                  bg={bgColor}
                                  p={3}
                                  borderRadius='lg'
                                  border='1px solid'
                                  borderColor={borderColor}
                                  boxShadow='lg'
                                >
                                  <HStack mb={2}>
                                    <Text fontSize='lg'>{item.emoji}</Text>
                                    <Text fontWeight='bold'>
                                      {item.month} '{item.year} - {item.season}
                                    </Text>
                                  </HStack>
                                  <VStack align='start' spacing={1}>
                                    <Text fontSize='sm'>
                                      <Text as='span' fontWeight='medium'>Volume:</Text> {item.count.toLocaleString()} reviews
                                    </Text>
                                    <HStack>
                                      <Text fontSize='sm' fontWeight='medium'>vs Average:</Text>
                                      <Badge 
                                        colorScheme={isAboveAvg ? 'green' : 'red'}
                                        fontSize='xs'
                                      >
                                        {isAboveAvg ? '+' : ''}{percentFromAvg.toFixed(1)}%
                                      </Badge>
                                    </HStack>
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <ReferenceLine 
                          y={avg} 
                          stroke='#718096' 
                          strokeDasharray='5 5' 
                          strokeWidth={2}
                          label={{ 
                            value: `Avg: ${Math.round(avg)}`, 
                            position: 'right',
                            fill: '#718096',
                            fontSize: 12
                          }}
                        />
                        <Area
                          type='monotone'
                          dataKey='count'
                          stroke='url(#seasonalLineGradient)'
                          strokeWidth={3}
                          fillOpacity={1}
                          fill='url(#seasonalGradient)'
                          activeDot={(props) => {
                            const { cx, cy, payload } = props;
                            return (
                              <circle
                                cx={cx}
                                cy={cy}
                                r={7}
                                fill={payload.lineColor}
                                stroke='white'
                                strokeWidth={2}
                              />
                            );
                          }}
                          isAnimationActive={true}
                          animationDuration={1000}
                          animationBegin={0}
                        />
                      </AreaChart>
                    );
                  })()}
                </ResponsiveContainer>
              </Box>

              {/* Legend for seasons */}
              <HStack justify='center' spacing={4} mt={2} mb={2} flexWrap='wrap'>
                <Text fontSize='xs' color={subtitleColor}>❄️ Winter</Text>
                <Text fontSize='xs' color={subtitleColor}>🌸 Spring</Text>
                <Text fontSize='xs' color={subtitleColor}>☀️ Summer</Text>
                <Text fontSize='xs' color={subtitleColor}>🍂 Fall</Text>
              </HStack>

              {/* Busy and Slow months summary */}
              {(() => {
                const data = metrics.activityTrendData || [];
                const avg = metrics.activityAverage || 0;
                const busyMonths = data.filter(d => d.count > avg).map(d => d.month);
                const slowMonths = data.filter(d => d.count < avg).map(d => d.month);
                
                return (
                  <HStack spacing={3} mt={2} align='stretch'>
                    {busyMonths.length > 0 && (
                      <Box
                        flex={1}
                        p={3}
                        bg='green.50'
                        borderRadius='md'
                        borderLeft='4px solid'
                        borderLeftColor='green.400'
                      >
                        <Text fontSize='sm' color='green.700'>
                          📈 <strong>Busy months:</strong> {busyMonths.join(', ')}
                        </Text>
                      </Box>
                    )}
                    {slowMonths.length > 0 && (
                      <Box
                        flex={1}
                        p={3}
                        bg='orange.50'
                        borderRadius='md'
                        borderLeft='4px solid'
                        borderLeftColor='orange.400'
                      >
                        <Text fontSize='sm' color='orange.700'>
                          📉 <strong>Slow months:</strong> {slowMonths.join(', ')}
                        </Text>
                      </Box>
                    )}
                  </HStack>
                );
              })()}
            </CardBody>
          </AnimatedBox>
        </SimpleGrid>

        {/* Month Snapshot - Full Width */}
        <Box mb={6}>
          <MonthSnapshot />
        </Box>

        {/* First Row - Ad Intelligence + Goals */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
          {/* Ad Intelligence Widget */}
          <AdIntelligenceWidget />

          {/* Goals Widget - Simple version for home section */}
          <SimpleGoalsWidget />
        </SimpleGrid>

        {/* Second Row - Content Templates + How to Beat Competitors (Both 50% width) */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={8}>
          {/* Content Templates Widget */}
          <ContentTemplatesWidget reviewsData={reviewsData} />

          {/* Actions Playbook Snippet - How to Beat Competitors */}
          <ActionsPlaybookSnippet ctaOnly={true} />
        </SimpleGrid>

        {/* Last Month's Leaders - Full Width */}
        <Box mb={6}>
          <LastMonthLeaders yearData={yearData} onBusinessClick={handleBusinessClick} />
        </Box>
          </>
        )}

        {/* Ranking Pie Charts Section (Home view only) */}
        {activeView === 'home' && (
          <>
        <AnimatedBox
          animation='fadeIn'
          animationKey='home-ranking-pie-charts'
          delay={0.4}
          duration={0.7}
          mb={8}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 3 }}
            spacing={6}
          >
            {/* Total Reviews Pie Chart Card */}
            <Card
              bg={bgColor}
              borderWidth='1px'
              borderColor={borderColor}
              borderRadius='lg'
              boxShadow='sm'
              transition='all 0.2s ease-in-out'
              _hover={{
                shadow: 'md',
                transform: 'translateY(-3px)'
              }}
              onClick={() => navigate('/dashboards/total-reviews')} // Navigate on click
              cursor='pointer' // Add pointer cursor
            >
              <CardHeader pb={2}>
                <Heading
                  size='md'
                  color={textColor}
                  textTransform="uppercase"
                  fontStyle="italic"
                  fontSize="1.3em"
                >
                  Total Reviews
                </Heading>
              </CardHeader>
              <CardBody>
                {totalReviewsPieData.length > 0 ? (
                  <Box h={{ base: '280px', md: '300px' }}>
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <PieChart>
                        <Pie
                          data={totalReviewsPieData}
                          dataKey='value'
                          nameKey='name'
                          cx='50%'
                          cy='50%'
                          outerRadius={80}
                          labelLine={true}
                          label={({ name }) => name}
                          isAnimationActive={true}
                          animationDuration={800}
                        >
                          {totalReviewsPieData.map((entry, index) => (
                            <Cell
                              key={`cell-total-reviews-${index}`}
                              fill={COLORS.charts[index % COLORS.charts.length]}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [
                            value.toLocaleString() + ' Reviews',
                            name
                          ]}
                        />
                        <Legend
                          content={<CustomPieLegend />}
                          wrapperStyle={{
                            fontSize: '12px',
                            paddingTop: '10px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Text color={subtitleColor}>
                    Total reviews data is not available.
                  </Text>
                )}
              </CardBody>
            </Card>

            {/* Website Traffic Pie Chart Card */}
            <Card
              bg={bgColor}
              borderWidth='1px'
              borderColor={borderColor}
              borderRadius='lg'
              boxShadow='sm'
              transition='all 0.2s ease-in-out'
              _hover={{
                shadow: 'md',
                transform: 'translateY(-3px)'
              }}
              onClick={() => navigate('/dashboards/web-traffic')}
              cursor='pointer'
            >
              <CardHeader pb={2}>
                <Heading
                  size='md'
                  color={textColor}
                  textTransform="uppercase"
                  fontStyle="italic"
                  fontSize="1.3em"
                >
                  Website Traffic
                </Heading>
              </CardHeader>
              <CardBody>
                {webTrafficPieData.length > 0 ? (
                  <Box h={{ base: '280px', md: '300px' }}>
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <PieChart>
                        <Pie
                          data={webTrafficPieData}
                          dataKey='value'
                          nameKey='name'
                          cx='50%'
                          cy='50%'
                          outerRadius={80}
                          labelLine={true}
                          label={({ name }) => name}
                          isAnimationActive={true}
                          animationDuration={800}
                        >
                          {webTrafficPieData.map((entry, index) => (
                            <Cell
                              key={`cell-web-traffic-${index}`}
                              fill={COLORS.charts[(index + 4) % COLORS.charts.length]}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [
                            value.toLocaleString() + ' Monthly Visitors',
                            name
                          ]}
                        />
                        <Legend
                          content={<CustomPieLegend />}
                          wrapperStyle={{
                            fontSize: '12px',
                            paddingTop: '10px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Text color={subtitleColor}>
                    Website traffic data is not available.
                  </Text>
                )}
              </CardBody>
            </Card>

            {/* Gross Growth Pie Chart Card */}
            <Card
              bg={bgColor}
              borderWidth='1px'
              borderColor={borderColor}
              borderRadius='lg'
              boxShadow='sm'
              transition='all 0.2s ease-in-out'
              _hover={{
                shadow: 'md',
                transform: 'translateY(-3px)'
              }}
              onClick={() => navigate('/dashboards/gross-growth')}
              cursor='pointer'
            >
              <CardHeader pb={2}>
                <Heading
                  size='md'
                  color={textColor}
                  textTransform="uppercase"
                  fontStyle="italic"
                  fontSize="1.3em"
                >
                  Review Growth
                </Heading>
              </CardHeader>
              <CardBody>
                {grossGrowthPieData.length > 0 ? (
                  <Box h={{ base: '280px', md: '300px' }}>
                    <ResponsiveContainer
                      width='100%'
                      height='100%'
                    >
                      <PieChart>
                        <Pie
                          data={grossGrowthPieData}
                          dataKey='value'
                          nameKey='name'
                          cx='50%'
                          cy='50%'
                          outerRadius={80}
                          labelLine={true}
                          label={({ name }) => name}
                          isAnimationActive={true}
                          animationDuration={800}
                        >
                          {grossGrowthPieData.map((entry, index) => (
                            <Cell
                              key={`cell-gross-growth-${index}`}
                              fill={
                                COLORS.charts[
                                  (index + 2) % COLORS.charts.length
                                ]
                              }
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [
                            value.toLocaleString() + ' Reviews Last Month',
                            name
                          ]}
                        />
                        <Legend
                          content={<CustomPieLegend />}
                          wrapperStyle={{
                            fontSize: '12px',
                            paddingTop: '10px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Text color={subtitleColor}>
                    Gross growth data is not available.
                  </Text>
                )}
              </CardBody>
            </Card>

          </SimpleGrid>
        </AnimatedBox>

        {/* Bottom Section - Detailed Lists */}
        <SimpleGrid
          columns={{ base: 1, lg: 3 }}
          spacing={6}
          mb={16} // Changed from mb={8} to mb={16} for more spacing
        >
          {/* Market Leaders */}
          <AnimatedBox
            animation='fadeIn'
            animationKey='home-market-leaders'
            delay={0.2}
            duration={0.6}
            as={Card}
            bg={bgColor}
            borderWidth='1px'
            borderColor={borderColor}
            borderRadius='lg'
            overflow='hidden'
            boxShadow='sm'
            _hover={{
              shadow: 'md',
              borderColor: 'blue.200'
            }}
            transition='all 0.3s'
            h='100%' // Added h="100%"
          >
            <CardHeader pb={0}>
              <Flex
                justify='space-between'
                align='center'
              >
                <Box
                  as='span'
                  display='inline-flex'
                  alignItems='center'
                >
                  <Icon
                    as={FaTrophy}
                    color='yellow.400'
                    mr={2}
                  />
                  <Heading
                    as={'span'}
                    size='md'
                    textTransform="uppercase"
                    fontStyle="italic"
                    fontSize="1.3em"
                  >
                    Market Leaders
                  </Heading>
                </Box>
                <Button
                  size='sm'
                  colorScheme='blue'
                  variant='ghost'
                  onClick={() => navigate('/dashboards/total-reviews')}
                >
                  Rankings
                </Button>
              </Flex>
            </CardHeader>
            <CardBody pt={6}>
              <VStack
                spacing={1}
                align='stretch'
              >
                {metrics.marketLeaders.slice(0, 5).map((leader, index) => (
                  <LeaderCard
                    key={index}
                    rank={index + 1}
                    business={leader}
                    bgColor={bgColor}
                    borderColor={borderColor}
                  />
                ))}
              </VStack>
            </CardBody>
          </AnimatedBox>

          {/* Recent Trends */}
          <AnimatedBox
            animation='fadeIn'
            animationKey='home-growth-trends'
            delay={0.3}
            duration={0.6}
            as={Card}
            bg={bgColor}
            borderWidth='1px'
            borderColor={borderColor}
            borderRadius='lg'
            overflow='hidden'
            boxShadow='sm'
            _hover={{
              shadow: 'md',
              borderColor: 'blue.200'
            }}
            transition='all 0.3s'
            h='100%' // Added h="100%"
          >
            <CardHeader pb={0}>
              <Flex
                justify='space-between'
                align='center'
              >
                <Box
                  as='span'
                  display='inline-flex'
                  alignItems='center'
                >
                  <Icon
                    as={FaChartLine}
                    color='blue.500'
                    mr={2}
                  />
                  <Heading
                    as={'span'}
                    size='md'
                    textTransform="uppercase"
                    fontStyle="italic"
                    fontSize="1.3em"
                  >
                    Growth Trends
                  </Heading>
                </Box>
                <Button
                  size='sm'
                  colorScheme='blue'
                  variant='ghost'
                  onClick={() => navigate('/dashboards/gross-growth')}
                >
                  Details
                </Button>
              </Flex>
            </CardHeader>
            <CardBody pt={6}>
              <VStack
                spacing={1}
                align='stretch'
              >
                {metrics.recentTrends.slice(0, 5).map((trend, index) => (
                  <Card
                    bg={bgColor}
                    borderWidth='1px'
                    borderColor={borderColor}
                    borderRadius='lg'
                    boxShadow='sm'
                    overflow='hidden'
                    transition='all 0.2s'
                    _hover={{
                      transform: 'translateY(-2px)',
                      shadow: 'md',
                      borderColor: 'blue.400'
                    }}
                    cursor='pointer'
                    mb={2}
                    key={index}
                  >
                    <CardBody
                      py={5} // Changed from py={4}
                      px={4}
                    >
                      <Flex
                        justify='space-between'
                        align='center'
                      >
                        <VStack
                          justify={'start'}
                          align='start'
                          spacing={1.5} // Changed from spacing={1}
                        >
                          <Text
                            as='span'
                            fontWeight='medium'
                            noOfLines={1}
                          >
                            <BusinessTextRenderer 
                              businesses={reviewsData} 
                              onBusinessClick={handleBusinessClick}
                            >
                              {trend.name}
                            </BusinessTextRenderer>
                          </Text>
                          <Text
                            mb={0}
                            fontSize='xs'
                            color={subtitleColor}
                          >
                            {trend.lastMonth} reviews last month (vs{' '}
                            {trend.previousMonth})
                          </Text>
                        </VStack>
                        <Badge
                          colorScheme={trend.trend >= 0 ? 'green' : 'red'}
                          variant='subtle'
                          px={2}
                          py={0.5}
                          borderRadius='full'
                          display='flex'
                          alignItems='center'
                        >
                          <Box
                            as='span'
                            display='inline-flex'
                            alignItems='center'
                          >
                            <Icon
                              as={trend.trend >= 0 ? FaArrowUp : FaArrowDown}
                              boxSize={2.5}
                            />
                            <Box
                              as='span'
                              ml='2px'
                            >
                              {Math.abs(trend.trend).toFixed(1)}%
                            </Box>
                          </Box>
                        </Badge>
                      </Flex>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </CardBody>
          </AnimatedBox>

          {/* Business Categories */}
          <AnimatedBox
            animation='fadeIn'
            animationKey='home-business-types'
            delay={0.4}
            duration={0.6}
            as={Card}
            bg={bgColor}
            borderWidth='1px'
            borderColor={borderColor}
            borderRadius='lg'
            overflow='hidden'
            boxShadow='sm'
            _hover={{
              shadow: 'md',
              borderColor: 'blue.200'
            }}
            transition='all 0.3s'
            h='100%' // Added h="100%"
          >
            <CardHeader pb={0}>
              <Flex
                justify='space-between'
                align='center'
              >
                <Box
                  as='span'
                  display='inline-flex'
                  alignItems='center'
                >
                  <Icon
                    as={FaStoreAlt}
                    color='blue.500'
                    mr={2}
                  />
                  <Heading
                    as={'span'}
                    size='md'
                    textTransform="uppercase"
                    fontStyle="italic"
                    fontSize="1.3em"
                  >
                    Business Types
                  </Heading>
                </Box>
                <Button
                  size='sm'
                  colorScheme='blue'
                  variant='ghost'
                  onClick={() => navigate('/dashboards/business-insights')}
                >
                  Explore
                </Button>
              </Flex>
            </CardHeader>
            <CardBody pt={6}>
              <VStack
                spacing={1}
                align='stretch'
              >
                {metrics.primaryTypeDistribution
                  .slice(0, 5)
                  .map((category, index) => (
                    <Card
                      bg={bgColor}
                      borderWidth='1px'
                      borderColor={borderColor}
                      borderRadius='lg'
                      boxShadow='sm'
                      overflow='hidden'
                      transition='all 0.2s'
                      _hover={{
                        transform: 'translateY(-2px)',
                        shadow: 'md',
                        borderColor: 'blue.400'
                      }}
                      cursor='pointer'
                      mb={2}
                      key={category.name}
                    >
                      <CardBody
                        py={5} // Changed from py={4}
                        px={4}
                        minH={'66px'}
                      >
                        <VStack
                          spacing={2}
                          align='stretch'
                        >
                          <Flex
                            justify='space-between'
                            align='center'
                          >
                            <Text
                              as='span'
                              fontWeight='medium'
                              noOfLines={1}
                            >
                              {category.name}
                            </Text>
                            <Text
                              fontSize='sm'
                              fontWeight='bold'
                              as={'span'}
                            >
                              {category.value}%
                            </Text>
                          </Flex>

                          <Box
                            w='100%'
                            h='8px'
                            bg='gray.100'
                            borderRadius='full'
                            overflow='hidden'
                          >
                            <Box
                              w={`${category.value}%`}
                              h='100%'
                              bg={COLORS.charts[index % COLORS.charts.length]}
                              borderRadius='full'
                            />
                          </Box>
                        </VStack>
                      </CardBody>
                    </Card>
                  ))}
              </VStack>
            </CardBody>
          </AnimatedBox>
        </SimpleGrid>

        {/* Reading List Widget - Supplementary Content */}
        <Box mb={8}>
          <ReadingListWidget />
        </Box>

        {/* Dashboard Cards - Navigate to All Dashboards */}
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={6}
          mb={16}
        >
          {dashboardData.map((dashboard, index) => (
            <DashboardCard
              key={dashboard.id}
              title={dashboard.title}
              icon={dashboard.icon}
              description={dashboard.description}
              link={dashboard.link}
              color={dashboard.color}
              bgImage={
                dashboard.id === 'business-insights'
                  ? "url('/output.png')"
                  : undefined
              }
              index={index}
            />
          ))}
        </SimpleGrid>
          </>
        )}
      </Box>

      {/* Floating Feedback Button - Sibling to the main content Box */}
      <Tooltip
        label='Share Your Feedback'
        placement='left'
        hasArrow
      >
        <Button
          position='fixed'
          bottom='30px'
          right='30px'
          size='lg'
          colorScheme='blue'
          borderRadius='full'
          boxShadow='lg'
          onClick={() => navigate('/roadmap')}
          leftIcon={<Icon as={FaComment} />}
          aria-label='Share Feedback'
          zIndex='overlay'
          _hover={{ transform: 'scale(1.05)' }}
          _active={{ transform: 'scale(0.95)' }}
        >
          Feedback
        </Button>
      </Tooltip>

      {/* Business Review Modal */}
      <BusinessReviewModal
        isOpen={isBusinessModalOpen}
        onClose={() => setIsBusinessModalOpen(false)}
        business={selectedBusiness}
        yearData={yearData}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
      />

      {/* Welcome Notification Popup - Top Right Corner */}
      <SlideFade in={welcomeMessage && showWelcome} offsetY='-20px'>
        <Box
          position='fixed'
          top={4}
          right={4}
          zIndex={9999}
          maxW='400px'
          minW='320px'
        >
          <Box
            bg={welcomeBg}
            borderWidth='1px'
            borderColor={welcomeBorder}
            borderRadius='lg'
            p={4}
            boxShadow='xl'
            position='relative'
            overflow='hidden'
          >
            {/* Close button */}
            <CloseButton
              position='absolute'
              top={2}
              right={2}
              size='sm'
              onClick={() => setShowWelcome(false)}
              color={welcomeButtonColor}
              _hover={{ bg: welcomeButtonHoverBg }}
            />
            
            <HStack spacing={3} pr={6}>
              <Icon
                as={FaLightbulb}
                boxSize={5}
                color={welcomeIconColor}
              />
              <Box flex={1}>
                <HStack spacing={2} mb={1} flexWrap='wrap'>
                  <Heading
                    size='sm'
                    color={welcomeHeadingColor}
                  >
                    {welcomeMessage?.greeting}
                  </Heading>
                  {activity?.login_streak_days > 0 && (
                    <Badge
                      colorScheme={activity.login_streak_days >= 3 ? 'orange' : 'blue'}
                      fontSize='xs'
                      px={2}
                      py={0.5}
                      display='flex'
                      alignItems='center'
                      gap={1}
                    >
                      {activity.login_streak_days >= 3 && '🔥'}
                      {activity.login_streak_days} day streak
                    </Badge>
                  )}
                </HStack>
                <Text
                  fontSize='sm'
                  color={welcomeTextColor}
                >
                  {welcomeMessage?.message}
                </Text>
              </Box>
            </HStack>
            
            {/* Progress bar showing auto-dismiss countdown */}
            <Box
              position='absolute'
              bottom={0}
              left={0}
              right={0}
              height='3px'
              bg={welcomeBorder}
              overflow='hidden'
            >
              <Box
                height='100%'
                bg='blue.400'
                animation='shrinkWidth 10s linear forwards'
                sx={{
                  '@keyframes shrinkWidth': {
                    '0%': { width: '100%' },
                    '100%': { width: '0%' }
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </SlideFade>
    </>
  );
};

export default HomePage;
